import { Box, Button, Typography, CircularProgress, Dialog, DialogTitle, DialogContent, IconButton } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { MatchingProfileSteps } from "./matching-profile-steps";
import DashboardHeader from "../../components/dashboard-header";
import { PersonalityQuiz, PersonalityQuizRef } from "../../components/personality-quiz";
import { StyleQuiz } from "../../components/style-quiz";
import { DesignerStyleQuiz, DesignerStyleQuizRef } from "../../components/designer-style-quiz";
import { ExpertiseQuiz, ExpertiseQuizRef } from "../../components/expertise-quiz";
import { WorkStyleQuiz, WorkStyleQuizRef } from "../../components/work-style-quiz";
import { ProjectQuiz } from "../../components/project-quiz";
import { HomeownerPreferencesQuiz } from "../../components/homeowner-preferences-quiz";
import HomeownerPriorities from "../../components/homeowner-priorities/HomeownerPriorities";
import { PreferencesQuiz, PreferencesQuizRef } from "../../components/preferences-quiz";
import MatchingProfileSuccess from "../../components/matching-profile-success";
import { useUserContext } from "../../hooks/userContext";
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { useDashboardFooter } from "../../contexts/DashboardFooterContext";
import { useQuery, useMutation } from "@apollo/client";
import { 
  GET_HOMEOWNER_STEP_STATUS, 
  GET_HOMEOWNER_MATCHING_PROFILE,
  CREATE_HOMEOWNER_MATCHING_PROFILE, 
  UPDATE_HOMEOWNER_MATCHING_PROFILE 
} from "../../graphql/homeowner-matching-profile";
// Removed unused GraphQL imports - now using custom hooks
import { useGetDesignerStepStatusQuery } from "../../hooks/useGetDesignerStepStatusQuery";
import { useCreateDesignerMatchingProfileMutation } from "../../hooks/useCreateDesignerMatchingProfileMutation";
import { useUpdateDesignerMatchingProfileMutation } from "../../hooks/useUpdateDesignerMatchingProfileMutation";
import { useGetDesignerOnboardingStatusQuery } from "../../hooks/useGetDesignerOnboardingStatusQuery";
import { useDesignerOnboardingAccess } from "../../hooks/useDesignerOnboardingAccess";
import { useDesignerApplicationContext } from "../../hooks/designer-application-context";
import type { DesignerMatchingProfile as DesignerMatchingProfileType, HomeOwnerMatchingProfile } from "../../__generated__/types";
import { DesignerMatchingProfileRetakeSummary } from "../../components/designer-matching-profile-retake-summary";
import { HomeownerMatchingProfileRetakeSummary } from "../../components/homeowner-matching-profile-retake-summary";
import { GET_DESIGNER_MATCHING_PROFILE } from "../../graphql/designer-matching-profile";
import { useNavigate } from "react-router";
import { Pages } from "../../utils";
import { useNotification } from "../../contexts/NotificationContext";
import { sanitizeStyleValue } from "../../utils/style-utils";

type DesignerStepCompletionState = {
  personalityCompleted: boolean;
  expertiseCompleted: boolean;
  styleCompleted: boolean;
  workStyleCompleted: boolean;
  preferencesCompleted: boolean;
};

const createEmptyDesignerStepCompletion = (): DesignerStepCompletionState => ({
  personalityCompleted: false,
  expertiseCompleted: false,
  styleCompleted: false,
  workStyleCompleted: false,
  preferencesCompleted: false,
});

type HomeownerStepCompletionState = {
  personalityCompleted: boolean;
  styleCompleted: boolean;
  projectCompleted: boolean;
  preferenceCompleted: boolean;
  prioritiesCompleted: boolean;
};

const createEmptyHomeownerStepCompletion = (): HomeownerStepCompletionState => ({
  personalityCompleted: false,
  styleCompleted: false,
  projectCompleted: false,
  preferenceCompleted: false,
  prioritiesCompleted: false,
});

const normalizeHomeownerStepCompletion = (
  step?: Partial<HomeownerStepCompletionState> | null
): HomeownerStepCompletionState => ({
  personalityCompleted: !!step?.personalityCompleted,
  styleCompleted: !!step?.styleCompleted,
  projectCompleted: !!step?.projectCompleted,
  preferenceCompleted: !!step?.preferenceCompleted,
  prioritiesCompleted: !!step?.prioritiesCompleted,
});

const isHomeownerProfileCompleted = (step?: HomeownerStepCompletionState | null) =>
  !!step &&
  step.personalityCompleted &&
  step.styleCompleted &&
  step.projectCompleted &&
  step.preferenceCompleted &&
  step.prioritiesCompleted;

const mapHomeownerCurrentStep = (step?: number | null) => {
  if (!step || step <= 1) return 1;
  if (step === 2) return 2;
  if (step === 3) return 3;
  if (step === 4) return 4;
  return 5;
};

// Helper function to remove __typename from GraphQL query results
const removeTypename = <T extends any>(obj: any): T => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) {
    return obj.map(item => removeTypename(item)) as any;
  }
  const { __typename, ...rest } = obj;
  const cleaned: any = {};
  for (const [key, value] of Object.entries(rest)) {
    if (value !== null && typeof value === 'object') {
      cleaned[key] = removeTypename(value);
    } else {
      cleaned[key] = value;
    }
  }
  return cleaned as T;
};

const mapCompetencyForGraphQL = (competency?: Record<string, string>) => {
  if (!competency) return {};

  const {
    environmentallySustainable,
    ...rest
  } = competency;

  return {
    ...rest,
    ...(environmentallySustainable !== undefined
      ? { evironmentallySustainable: environmentallySustainable }
      : {}),
  };
};

// Normalize preferences values to match current GraphQL schema
const normalizePetPreference = (value: any): string | undefined => {
  const allowed = ['dog', 'cat', 'fish', 'none'];
  if (!value) return undefined;
  const petValue = Array.isArray(value) ? value[0] : value;
  const asString = String(petValue);
  return allowed.includes(asString) ? asString : undefined;
};

const normalizeMinorityOwnedPreference = (value: any): boolean | undefined => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const trimmed = value.trim().toLowerCase();
    if (!trimmed || trimmed === 'n/a') return undefined;
    if (trimmed === 'true') return true;
    if (trimmed === 'false') return false;
    // Legacy string values indicated a positive minority-owned response
    return true;
  }
  return undefined;
};

const isPriorityHireLikelihoodValue = (
  value: string | null | undefined
): boolean => {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) return false;
  return (
    normalized.includes("absolute") ||
    normalized.includes("highly likely") ||
    normalized.includes("warm opportunity") ||
    normalized.includes("not likely")
  );
};

// Partnership style values: UI/GraphQL use lowInvolvement/collaborative/highlyInvolved.
// Support legacy persisted values (fullService/consultant/eDesign) by mapping them back to UI-friendly keys.
const partnershipStyleUiToBackend: Record<string, string> = {
  lowInvolvement: 'lowInvolvement',
  collaborative: 'collaborative',
  highlyInvolved: 'highlyInvolved',
};

const partnershipStyleBackendToUi: Record<string, string> = {
  lowInvolvement: 'lowInvolvement',
  collaborative: 'collaborative',
  highlyInvolved: 'highlyInvolved',
  // legacy values
  fullService: 'lowInvolvement',
  consultant: 'collaborative',
  eDesign: 'highlyInvolved',
};

/**
 * Helper function to normalize previousDesignerProfile into quiz state shapes
 * This allows preloading saved answers when designers retake the matching profile
 */
const buildInitialDesignerQuizState = (profile: DesignerMatchingProfileType | null) => {
  if (!profile) return null;

  return {
    // Personality Quiz - already in correct format
    personalityType: profile.personalityType || null,
    
    // Expertise Quiz - normalize from backend format
    expertise: profile.expertise ? {
      projectTypes: (profile.expertise.projectTypes?.filter(Boolean) || []).map((t: any) => String(t)),
      projectRankings: profile.expertise.projectRankings?.map((r: any) => ({
        category: String(r?.category || ''),
        ranking: Number(r?.ranking || 0)
      })).filter((r: any) => r.category) || [],
      experience: (profile.expertise.experience?.filter(Boolean) || []).map((e: any) => String(e)),
      experienceRankings: profile.expertise.experienceRankings?.map((r: any) => ({
        experience: String(r?.experience || ''),
        ranking: Number(r?.ranking || 0)
      })).filter((r: any) => r.experience) || [],
      // propertyRankings is derived from experienceRankings (same data, different field name for UI)
      propertyRankings: profile.expertise.experienceRankings?.map((r: any) => ({
        property: String(r?.experience || ''), // Map experience to property for UI
        ranking: Number(r?.ranking || 0)
      })).filter((r: any) => r.property) || [],
      competency: profile.expertise.competency ? (() => {
        const rawCompetency = profile.expertise.competency;
        
        // Extract only the 14 competency level fields (excluding __typename and strengths)
        const competencyValues = {
          accessibility: rawCompetency.accessibility,
          petFriendly: rawCompetency.petFriendly,
          smartHome: rawCompetency.smartHome,
          customStorage: rawCompetency.customStorage,
          specificAcoustic: rawCompetency.specificAcoustic,
          environmentallySustainable: rawCompetency.evironmentallySustainable,
          uniqueLightingEffects: rawCompetency.uniqueLightingEffects,
          historicalPreservation: rawCompetency.historicalPreservation,
          outdoorLiving: rawCompetency.outdoorLiving,
          specializedVentilation: rawCompetency.specializedVentilation,
          fengShui: rawCompetency.fengShui,
          artIntegration: rawCompetency.artIntegration,
          wineCellar: rawCompetency.wineCellar,
          homeGym: rawCompetency.homeGym,
        };
        
        
        const competencyEntries = Object.entries(competencyValues);
        
        
        const filteredEntries = competencyEntries.filter(([_, v]) => v != null && v !== undefined);
        
        const mappedEntries = filteredEntries.map(([k, v]) => [k, String(v)]);
        
        const competencyMap = Object.fromEntries(mappedEntries) as Record<string, string>;
        return competencyMap;
      })() : {},
      strengths: (() => {
        // Strengths are nested inside competency in the backend schema
        const strengthsArray = profile.expertise.competency?.strengths?.map((s: any) => {
          // Remove GraphQL __typename field
          const { __typename, ...cleanStrength } = s || {};
          return {
            strength: cleanStrength?.strength || '',
            ranking: cleanStrength?.ranking || 0
          };
        }).filter((s: any) => s.strength) || [];
        return strengthsArray;
      })(),
    } : null,
    
    // Designer Style Quiz - normalize from backend format
    style: profile.style ? {
      canDesignAnyStyle: profile.style.anyStyle ?? null,
      // If the style object exists, treat missing knownForStyle as an explicit "No"
      hasKnownStyle: profile.style.knownForStyle !== null && profile.style.knownForStyle !== undefined
        ? true
        : (profile.style ? false : null),
      knownStyle: sanitizeStyleValue(profile.style.knownForStyle || ''),
      expertiseStyles: (profile.style.strongStyles?.filter(Boolean) || [])
        .map((s: any) => sanitizeStyleValue(String(s)))
        .filter(Boolean),
      styleRankings: profile.style.styleRankings?.map((r: any) => sanitizeStyleValue(String(r?.style || ''))).filter(Boolean) || [],
    } : null,
    
    // Work Style Quiz - normalize from backend format
    workStyle: profile.workStyle ? {
      partnershipOptions: (profile.workStyle.partnershipOptions?.filter(Boolean) || []).map((p: any) => String(p)),
      compensationModel: (profile.workStyle.compensationModel?.filter(Boolean) || []).map((c: any) => String(c)),
      presentationType: (profile.workStyle.presentationType?.filter(Boolean) || []).map((p: any) => String(p)),
      minimumBudget: profile.workStyle.minimumBudget ? String(profile.workStyle.minimumBudget) : undefined,
    } : null,
    
    // Preferences Quiz - normalize from backend format
    preferences: profile.preferences ? {
      social: {
        pronouns: profile.preferences.social?.pronouns ? String(profile.preferences.social.pronouns) : undefined,
        pets: (() => {
          const pet = normalizePetPreference(profile.preferences.social?.pets);
          return pet ? [pet] : undefined;
        })(),
        lgbtqPositive: profile.preferences.social?.lgbtqPositive ?? undefined,
        womenOwned: profile.preferences.social?.womenOwned ?? undefined,
        minorityOwned: normalizeMinorityOwnedPreference(profile.preferences.social?.minorityOwned),
        preferToWorkWithRace: profile.preferences.social?.preferToWorkWithRace ? String(profile.preferences.social.preferToWorkWithRace) : undefined,
        politicalAffiliation: profile.preferences.social?.politicalAffiliation ? String(profile.preferences.social.politicalAffiliation) : undefined,
        otherPoliticalAffiliation: profile.preferences.social?.otherPoliticalAffiliation || undefined,
        preferSamePolitics: profile.preferences.social?.preferSamePolitics ?? undefined,
      },
      partnershipStyleRankings: profile.preferences.partnershipStyleRankings?.map((r: any) => ({
        partnershipStyle: partnershipStyleBackendToUi[String(r?.partnershipStyle || '')] || String(r?.partnershipStyle || ''),
        ranking: Number(r?.ranking || 0)
      })).filter((r: any) => r.partnershipStyle) || [],
      acquisitionPreferences: (profile.preferences.acquisitionPreferences?.filter(Boolean) || []).map((a: any) => String(a)),
      acquisitionRankings: profile.preferences.acquisitionRankings?.map((r: any) => ({
        acquisitionPreference: String(r?.acquisitionPreference || ''),
        ranking: Number(r?.ranking || 0)
      })).filter((r: any) => r.acquisitionPreference) || [],
      ageRange: profile.preferences.ageRange ? String(profile.preferences.ageRange) : undefined,
      children: profile.preferences.children ?? undefined,
      zipCodes: (profile.preferences.zipCodes?.filter(Boolean) || []).map((z: any) => String(z)),
    } : null,
  };
};

/**
 * Helper function to normalize previousHomeownerProfile into quiz state shapes
 * This allows preloading saved answers when homeowners retake the matching profile
 */
const buildInitialHomeownerQuizState = (profile: HomeOwnerMatchingProfile | null) => {
  if (!profile) return null;

  return {
    // Personality Quiz - already in correct format
    personalityType: profile.personalityType || null,
    
    // Style Quiz - normalize from backend format
    style: profile.homeOwnerStyles ? {
      homeOwnerStyle: (profile.homeOwnerStyles.homeOwnerStyle?.filter(Boolean) || []).map((s: any) => String(s)),
      favoriteLook: profile.homeOwnerStyles.favoriteLook || undefined,
      selectedStyles: (profile.homeOwnerStyles.selectedStyles?.filter(Boolean) || []).map((s: any) => String(s)),
      selectedImages: Array.isArray(profile.homeOwnerStyles.selectedImages)
        ? profile.homeOwnerStyles.selectedImages
            .map((item: any) => {
              const style = item?.style ? String(item.style) : '';
              const imageIndex = Number(item?.imageIndex);
              return {
                style,
                imageIndex: Number.isFinite(imageIndex) ? imageIndex : undefined,
                src: item?.src ? String(item.src) : undefined,
              };
            })
            .filter((item: any) => item.style)
        : [],
      matchupResults: Array.isArray(profile.homeOwnerStyles.matchupResults)
        ? profile.homeOwnerStyles.matchupResults
            .map((item: any) => ({
              style1: item?.style1 ? String(item.style1) : '',
              style2: item?.style2 ? String(item.style2) : '',
              winner: item?.winner ? String(item.winner) : '',
            }))
            .filter((item: any) => item.style1 && item.style2 && item.winner)
        : [],
      finalSelections: Array.isArray(profile.homeOwnerStyles.finalSelections)
        ? profile.homeOwnerStyles.finalSelections
            .map((item: any) => {
              const style = item?.style ? String(item.style) : '';
              const imageIndex = Number(item?.imageIndex);
              return {
                style,
                imageIndex: Number.isFinite(imageIndex) ? imageIndex : undefined,
                src: item?.src ? String(item.src) : undefined,
              };
            })
            .filter((item: any) => item.style)
        : [],
    } : null,

    // Project Quiz - normalize from backend format
    project: profile.projectType ? {
      projectType: Array.isArray(profile.projectType.projectType)
        ? String(profile.projectType.projectType[0] || '')
        : profile.projectType.projectType
        ? String(profile.projectType.projectType)
        : undefined,
      designItems: (profile.projectType.designItems?.filter(Boolean) || []).map((d: any) => String(d)),
      specialNeeds: (profile.projectType.specialNeeds?.filter(Boolean) || []).map((s: any) => String(s)),
      conceptPresentation: (() => {
        const raw = profile.projectType.conceptPresentation;
        if (!raw) return undefined;
        return Array.isArray(raw) ? raw.map((v: any) => String(v)) : [String(raw)];
      })(),
      projectAddress: profile.projectType.projectAddress
        ? {
            id: profile.projectType.projectAddress.id || undefined,
            streetAddress1: profile.projectType.projectAddress.streetAddress1 || '',
            streetAddress2: profile.projectType.projectAddress.streetAddress2 || '',
            city: profile.projectType.projectAddress.city || '',
            state: profile.projectType.projectAddress.state || '',
            zipCode: profile.projectType.projectAddress.zipCode || '',
          }
        : undefined,
    } : null,
    
    // Preferences Quiz - normalize from backend format
    preferences: profile.preferences ? {
      budgetRange: profile.preferences.budgetRange ? String(profile.preferences.budgetRange) : undefined,
      billingMethods: (profile.preferences.billingMethods?.filter(Boolean) || []).map((b: any) => String(b)),
      wantsFinancing: profile.preferences.wantsFinancing ? String(profile.preferences.wantsFinancing) : undefined,
      partnershipStyle: profile.preferences.partnershipStyle ? String(profile.preferences.partnershipStyle) : undefined,
      designerRole: profile.preferences.designerRole ? String(profile.preferences.designerRole) : undefined,
      skillRankings: (profile.preferences.skillRankings?.filter(Boolean) || []).map((s: any) => String(s)),
      acquisitionPreference: profile.preferences.acquisitionPreference ? String(profile.preferences.acquisitionPreference) : undefined,
      designerGenderPreference: profile.preferences.designerGenderPreference ? String(profile.preferences.designerGenderPreference) : undefined,
      designerPetPreference: (profile.preferences.designerPetPreference?.filter(Boolean) || []).map((p: any) => String(p)),
      lgbtqFriendly: profile.preferences.lgbtqFriendly ? String(profile.preferences.lgbtqFriendly) : undefined,
      womenOwnedFirm: profile.preferences.womenOwnedFirm ? String(profile.preferences.womenOwnedFirm) : undefined,
      minorityOwnedPreference: profile.preferences.minorityOwnedPreference ? String(profile.preferences.minorityOwnedPreference) : undefined,
      samePolitics: profile.preferences.samePolitics ? String(profile.preferences.samePolitics) : undefined,
      politicalAffiliation: profile.preferences.politicalAffiliation ? String(profile.preferences.politicalAffiliation) : undefined,
      sameAgeRangePreference: profile.preferences.sameAgeRangePreference ? String(profile.preferences.sameAgeRangePreference) : undefined,
      desiredAgeRange: profile.preferences.desiredAgeRange ? String(profile.preferences.desiredAgeRange) : undefined,
      hasChildrenPreference: profile.preferences.hasChildrenPreference ? String(profile.preferences.hasChildrenPreference) : undefined,
      experiencePreference: profile.preferences.experiencePreference ? String(profile.preferences.experiencePreference) : undefined,
      hireReason: (profile.preferences as any)?.hireReason ? String((profile.preferences as any).hireReason) : undefined,
      hireLikelihood: profile.preferences.hireLikelihood ? String(profile.preferences.hireLikelihood) : undefined,
    } : null,
    
    // Priorities - already in correct format
    priorities: profile.priorities?.order ? profile.priorities.order.filter(Boolean).map((p: any) => String(p)) : null,
  };
};

export const MyMatchingProfilePage = () => {
  const navigate = useNavigate();

  const { user } = useUserContext();
  const { showError } = useNotification();
  const isHomeowner = (user?.accountType?.toLowerCase() || "") === "homeowner";
  const { isMatchingProfileUnlocked, isBioUnlocked } = useDesignerOnboardingAccess();
  const { application, hasApplicationDataLoaded } = useDesignerApplicationContext();
  
  // Use the new custom hook for designer step status - need to check if profile exists early
  const { 
    stepStatus: designerStepStatusData, 
    loading: designerStepStatusLoading, 
    error: designerStepStatusError,
    refetch: refetchDesignerStepStatus 
  } = useGetDesignerStepStatusQuery(user?.id || "");
  
  // Check if user has a completed matching profile - they should always be able to retake it
  // Only evaluate this if data has loaded (not loading)
  const hasCompletedProfile = !designerStepStatusLoading && 
    !isHomeowner && 
    designerStepStatusData?.profile?.stepCompletion && 
    designerStepStatusData.profile.stepCompletion.personalityCompleted &&
    designerStepStatusData.profile.stepCompletion.expertiseCompleted &&
    designerStepStatusData.profile.stepCompletion.styleCompleted &&
    designerStepStatusData.profile.stepCompletion.workStyleCompleted &&
    designerStepStatusData.profile.stepCompletion.preferencesCompleted;
  
  // Route guard: Redirect if matching profile is locked for designers
  // BUT: Users with completed profiles should ALWAYS be able to access it for retake
  // Only redirect if:
  // 1. User is a designer
  // 2. User ID is available
  // 3. Application data has loaded
  // 4. Designer step status has loaded (to check if profile is completed)
  // 5. User doesn't have a completed profile (can't retake)
  // 6. Matching profile is locked (not approved)
  useEffect(() => {
    // Don't redirect if:
    // - Not a designer
    // - User ID not available
    // - Application data hasn't loaded yet
    // - Designer step status is still loading (wait for it to load)
    // - User has a completed profile (should always be able to retake)
    // - Matching profile is unlocked
    if (user?.accountType === 'Designer' && 
        user?.id && 
        hasApplicationDataLoaded && 
        !designerStepStatusLoading && // Wait for step status to load
        !hasCompletedProfile && 
        !isMatchingProfileUnlocked) {
      // Add a small delay to ensure all data has fully loaded before redirecting
      const redirectTimer = setTimeout(() => {
        navigate(Pages.DASHBOARD);
      }, 200);
      
      return () => clearTimeout(redirectTimer);
    }
  }, [user?.accountType, user?.id, hasApplicationDataLoaded, hasCompletedProfile, isMatchingProfileUnlocked, application?.status, navigate, designerStepStatusData, designerStepStatusLoading]);
  const totalSteps = isHomeowner ? 5 : 5; // Both have 5 steps, but different content
  const [mainStep, setMainStep] = useState<number>(1);
  const [personalityResult, setPersonalityResult] = useState<any>(null);
  const [viewResultsDialogOpen, setViewResultsDialogOpen] = useState<boolean>(false);
  const [viewHomeownerResultsDialogOpen, setViewHomeownerResultsDialogOpen] = useState<boolean>(false);

  const [showingBegin, setShowingBegin] = useState<boolean>(true);
  const [profileId, setProfileId] = useState<string | null>(null);
  const [isRetakeInProgress, setIsRetakeInProgress] = useState<boolean>(false);
  const [isHomeownerRetakeInProgress, setIsHomeownerRetakeInProgress] = useState<boolean>(false);

  const [designerStepCompletion, setDesignerStepCompletion] = useState<DesignerStepCompletionState>(() =>
    createEmptyDesignerStepCompletion()
  );
  const designerStepCompletionRef = useRef<DesignerStepCompletionState>(designerStepCompletion);

  const syncDesignerStepCompletion = useCallback((next: DesignerStepCompletionState) => {
    designerStepCompletionRef.current = next;
    setDesignerStepCompletion(next);
  }, []);

  // Homeowner step completion tracking (similar to designer)
  const [homeownerStepCompletion, setHomeownerStepCompletion] = useState<HomeownerStepCompletionState>(() =>
    createEmptyHomeownerStepCompletion()
  );
  const homeownerStepCompletionRef = useRef<HomeownerStepCompletionState>(homeownerStepCompletion);

  const syncHomeownerStepCompletion = useCallback((next: HomeownerStepCompletionState) => {
    const normalized = normalizeHomeownerStepCompletion(next);
    homeownerStepCompletionRef.current = normalized;
    setHomeownerStepCompletion(normalized);
  }, []);

  // GraphQL hooks for homeowner - only execute when we have a valid homeowner user ID
  const shouldFetchHomeownerStepStatus = isHomeowner && user?.id && user.id.trim() !== '';
  const shouldFetchDesignerStepStatus = !isHomeowner && user?.id && user.id.trim() !== '';
  
  
  const { 
    data: homeownerStepStatusData, 
    loading: homeownerStepStatusLoading, 
    error: homeownerStepStatusError,
    refetch: refetchHomeownerStepStatus 
  } = useQuery(GET_HOMEOWNER_STEP_STATUS, {
    variables: { homeOwnerId: user?.id || "" },
    skip: !shouldFetchHomeownerStepStatus,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all', // Continue even if there are GraphQL errors
    onError: (error) => {
      console.error('GraphQL Error in getHomeownerStepStatus:', error);
    },
    onCompleted: (data) => {
    }
  });
  
  
  // Check if homeowner has completed matching profile (for determining if we should fetch full profile)
  const hasPreviousHomeownerProfileForQuery = isHomeowner && !!(homeownerStepStatusData?.getHomeownerStepStatus?.profileExists);
  
  // Fetch full homeowner matching profile for review (only when profile exists)
  const { 
    data: homeownerProfileData, 
    loading: homeownerProfileLoading,
    refetch: refetchHomeownerProfile
  } = useQuery(GET_HOMEOWNER_MATCHING_PROFILE, {
    variables: { homeOwnerId: user?.id || "" },
    skip: !hasPreviousHomeownerProfileForQuery,
    fetchPolicy: 'cache-and-network',
  });

  // Note: designerStepStatusData is already fetched earlier (above) to check for completed profile in redirect guard

  // DISABLED: This query is failing with 400 errors and we're using designerStepStatusData.profile as primary source
  // We can re-enable this later once the backend issue is resolved
  // For now, we rely on designerStepStatusData.profile which contains all the same data
  const {
    data: designerProfileData,
    loading: designerProfileLoading,
    error: designerProfileError,
    refetch: refetchDesignerProfile,
  } = useQuery(GET_DESIGNER_MATCHING_PROFILE, {
    variables: { designerId: user?.id || "" },
    // Skip this query entirely since we're using designerStepStatusData.profile as primary source
    // This prevents 400 errors and unnecessary network requests
    skip: true, // Always skip - we use designerStepStatusData.profile instead
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all', // Continue even if there are GraphQL errors
    onError: (error) => {
      // Silently handle errors since this query is disabled
      console.warn('getDesignerMatchingProfile query error (query is disabled, using step status data instead):', error);
    }
  });

  const [cachedDesignerProfile, setCachedDesignerProfile] = useState<DesignerMatchingProfileType | null>(null);

  useEffect(() => {
    if (designerProfileData?.getDesignerMatchingProfile) {
      setCachedDesignerProfile(designerProfileData.getDesignerMatchingProfile);
    }
  }, [designerProfileData]);

  const refreshDesignerProfile = useCallback(async () => {
    // DISABLED: Since we're using designerStepStatusData.profile as primary source,
    // we should refresh step status instead of the matching profile query
    // This function is kept for backwards compatibility but doesn't do anything
    // since the query is disabled
    if (isHomeowner || !shouldFetchDesignerStepStatus) return;
    // No-op: The query is disabled, so there's nothing to refresh
    // Use refetchDesignerStepStatus() instead if you need to refresh profile data
    return Promise.resolve();
  }, [isHomeowner, shouldFetchDesignerStepStatus]);
  
// Debug log to see what designer step status data looks like
  
  // Load existing profileId from step status on mount
  useEffect(() => {
    
    if (!isHomeowner && designerStepStatusData?.profile?._id) {
      const existingProfileId = designerStepStatusData.profile._id;
      setProfileId(existingProfileId);
    } else if (isHomeowner && homeownerStepStatusData?.getHomeownerStepStatus?.profile?._id) {
      const existingProfileId = homeownerStepStatusData.getHomeownerStepStatus.profile._id;
      setProfileId(existingProfileId);
    } else if (!isHomeowner && designerStepStatusData && !designerStepStatusData.profile) {
    } else {
    }
  }, [isHomeowner, designerStepStatusData, homeownerStepStatusData]);

  useEffect(() => {
    if (isHomeowner) {
      return;
    }

    if (designerStepStatusData?.profile?.stepCompletion) {
      const serverStepCompletion: DesignerStepCompletionState = {
        personalityCompleted: !!designerStepStatusData.profile.stepCompletion.personalityCompleted,
        expertiseCompleted: !!designerStepStatusData.profile.stepCompletion.expertiseCompleted,
        styleCompleted: !!designerStepStatusData.profile.stepCompletion.styleCompleted,
        workStyleCompleted: !!designerStepStatusData.profile.stepCompletion.workStyleCompleted,
        preferencesCompleted: !!designerStepStatusData.profile.stepCompletion.preferencesCompleted,
      };

      const current = designerStepCompletionRef.current;
      if (
        serverStepCompletion.personalityCompleted !== current.personalityCompleted ||
        serverStepCompletion.expertiseCompleted !== current.expertiseCompleted ||
        serverStepCompletion.styleCompleted !== current.styleCompleted ||
        serverStepCompletion.workStyleCompleted !== current.workStyleCompleted ||
        serverStepCompletion.preferencesCompleted !== current.preferencesCompleted
      ) {
        syncDesignerStepCompletion(serverStepCompletion);
      }
  } else if (designerStepStatusData && !designerStepStatusData.profile) {
    const current = designerStepCompletionRef.current;
    const hasProgress =
      current.personalityCompleted ||
      current.expertiseCompleted ||
        current.styleCompleted ||
        current.workStyleCompleted ||
        current.preferencesCompleted;

      if (hasProgress) {
        syncDesignerStepCompletion(createEmptyDesignerStepCompletion());
    }
  }
}, [designerStepStatusData, isHomeowner, syncDesignerStepCompletion]);

  // Sync homeowner step completion from server data (similar to designer)
  useEffect(() => {
    if (!isHomeowner) {
      return;
    }

    if (homeownerStepStatusData?.getHomeownerStepStatus?.profile?.stepCompletion) {
      const serverStepCompletion = normalizeHomeownerStepCompletion({
        personalityCompleted:
          !!homeownerStepStatusData.getHomeownerStepStatus.profile.stepCompletion
            .personalityCompleted,
        styleCompleted:
          !!homeownerStepStatusData.getHomeownerStepStatus.profile.stepCompletion
            .styleCompleted,
        projectCompleted:
          !!homeownerStepStatusData.getHomeownerStepStatus.profile.stepCompletion
            .projectCompleted,
        preferenceCompleted:
          !!homeownerStepStatusData.getHomeownerStepStatus.profile.stepCompletion
            .preferenceCompleted,
        prioritiesCompleted:
          !!homeownerStepStatusData.getHomeownerStepStatus.profile.stepCompletion
            .prioritiesCompleted,
      });

      const current = homeownerStepCompletionRef.current;
      if (
        serverStepCompletion.personalityCompleted !== current.personalityCompleted ||
        serverStepCompletion.styleCompleted !== current.styleCompleted ||
        serverStepCompletion.projectCompleted !== current.projectCompleted ||
        serverStepCompletion.preferenceCompleted !== current.preferenceCompleted ||
        serverStepCompletion.prioritiesCompleted !== current.prioritiesCompleted
      ) {
        syncHomeownerStepCompletion(serverStepCompletion);
      }
    } else if (homeownerStepStatusData && !homeownerStepStatusData.getHomeownerStepStatus?.profile) {
      const current = homeownerStepCompletionRef.current;
      const hasProgress =
        current.personalityCompleted ||
        current.styleCompleted ||
        current.projectCompleted ||
        current.preferenceCompleted ||
        current.prioritiesCompleted;

      if (hasProgress) {
        syncHomeownerStepCompletion(createEmptyHomeownerStepCompletion());
      }
    }
  }, [homeownerStepStatusData, isHomeowner, syncHomeownerStepCompletion]);

  // Use cachedDesignerProfile if available, otherwise fallback to designerStepStatusData.profile
  // This ensures we can still preload answers even if the GET_DESIGNER_MATCHING_PROFILE query fails
  // Always prefer the most complete data available - use designerStepStatusData.profile as primary source
  // since it's more reliable and contains all sections
  const previousDesignerProfile = useMemo(() => {
    
    // Always prefer designerStepStatusData.profile as it's fetched reliably and contains all sections
    if (designerStepStatusData?.profile && !isHomeowner) {
      return designerStepStatusData.profile as DesignerMatchingProfileType;
    }
    // Fallback to cached profile from query if available
    if (cachedDesignerProfile) {
      return cachedDesignerProfile;
    }
    // Fallback to query data if step status is not available
    if (designerProfileData?.getDesignerMatchingProfile) {
      return designerProfileData.getDesignerMatchingProfile as DesignerMatchingProfileType;
    }
    return null;
  }, [cachedDesignerProfile, designerStepStatusData?.profile, designerProfileData?.getDesignerMatchingProfile, isHomeowner, user?.id, user?.email]);
  
  const hasPreviousDesignerProfile = !isHomeowner && !!(designerStepStatusData?.profileExists);
  
  // Check if homeowner has completed matching profile
  const hasPreviousHomeownerProfile = isHomeowner && !!(homeownerStepStatusData?.getHomeownerStepStatus?.profileExists);
  const homeownerProfile = homeownerStepStatusData?.getHomeownerStepStatus?.profile;
  const isHomeownerProfileComplete =
    isHomeowner && hasPreviousHomeownerProfile
      ? isHomeownerProfileCompleted(
          normalizeHomeownerStepCompletion(homeownerProfile?.stepCompletion)
        )
      : false;

  // Get previous homeowner profile (similar to previousDesignerProfile)
  const previousHomeownerProfile = useMemo(() => {
    
    // ALWAYS prefer homeownerProfileData as it has the full profile with all sections
    // including projectType.conceptPresentation
    if (homeownerProfileData?.getHomeOwnerMatchingProfile && isHomeowner) {
      return homeownerProfileData.getHomeOwnerMatchingProfile;
    }
    
    // ONLY use step status profile if full profile query is NOT expected to return data
    // or if it's finished loading with no result
    // Step status profile doesn't have projectType, so using it during retake causes data loss
    if (homeownerProfile && isHomeowner && !hasPreviousHomeownerProfileForQuery) {
      return homeownerProfile;
    }
    
    return null;
  }, [homeownerProfileData?.getHomeOwnerMatchingProfile, homeownerProfile, isHomeowner, user?.id, hasPreviousHomeownerProfileForQuery, homeownerProfileLoading]);
  
  
  const isDesignerProfileComplete =
    hasPreviousDesignerProfile &&
    designerStepCompletion.personalityCompleted &&
    designerStepCompletion.expertiseCompleted &&
    designerStepCompletion.styleCompleted &&
    designerStepCompletion.workStyleCompleted &&
    designerStepCompletion.preferencesCompleted;

  const isHomeownerContinuing =
    isHomeowner && hasPreviousHomeownerProfile && !isHomeownerProfileComplete;

  const designerPersonalityResult = previousDesignerProfile?.personalityType || null;
  const showDesignerPersonalityResultLanding =
    !isHomeowner &&
    isDesignerProfileComplete &&
    !isRetakeInProgress &&
    mainStep === 1 &&
    !!designerPersonalityResult;

  const homeownerPersonalityResult = previousHomeownerProfile?.personalityType || null;
  const showHomeownerPersonalityResultLanding =
    isHomeowner &&
    isHomeownerProfileComplete &&
    !isHomeownerRetakeInProgress &&
    mainStep === 1 &&
    !!homeownerPersonalityResult;

  const shouldPrefillHomeownerAnswers =
    isHomeowner && hasPreviousHomeownerProfile;

  // Build initial quiz state from previous profile (for preloading answers on retake)
  const initialDesignerAnswers = useMemo(() => {
    
    if (!hasPreviousDesignerProfile || !previousDesignerProfile) {
      return null;
    }
    
    const normalized = buildInitialDesignerQuizState(previousDesignerProfile);
    return normalized;
  }, [hasPreviousDesignerProfile, previousDesignerProfile]);

  // Build initial quiz state from previous homeowner profile (for preloading answers on retake)
  const initialHomeownerAnswers = useMemo(() => {
    
    if (!hasPreviousHomeownerProfile || !previousHomeownerProfile) {
      return null;
    }
    
    const normalized = buildInitialHomeownerQuizState(previousHomeownerProfile);
    return normalized;
  }, [hasPreviousHomeownerProfile, previousHomeownerProfile]);

  // Preload homeowner quiz state when hasPreviousHomeownerProfile (similar to designer)
  // IMPORTANT: During retake, we preload data but DO NOT mark as completed
  // This allows users to go through ALL steps
  // Users can review and modify their answers before submitting
  useEffect(() => {
    if (initialHomeownerAnswers && shouldPrefillHomeownerAnswers) {
      // Preload personality quiz result - but don't mark as completed
      if (initialHomeownerAnswers.personalityType) {
        setPersonalityResult(initialHomeownerAnswers.personalityType);
        // Don't set personalityCompleted - let user go through quiz
      }
      
      // Style quiz prefills via initialStyleValue in the component (no state needed here)
      
      // Preload preferences quiz result - but don't mark as completed
      if (initialHomeownerAnswers.preferences) {
        setPreferenceQuizResult(initialHomeownerAnswers.preferences);
        // Don't set preferenceCompleted - let user go through all steps
      }
      
      // Priorities are handled via initialOrder prop
    }
  }, [initialHomeownerAnswers, shouldPrefillHomeownerAnswers]);

  // Preload quiz state when hasPreviousDesignerProfile
  // IMPORTANT: During retake, we preload data but DO NOT mark as completed
  // This allows users to go through ALL steps including ranking steps
  // Users can review and modify their answers before submitting
  useEffect(() => {
    if (initialDesignerAnswers && isRetakeInProgress) {
      // Preload personality quiz result - but don't mark as completed
      // User will go through the quiz normally
      if (initialDesignerAnswers.personalityType) {
        setPersonalityResult(initialDesignerAnswers.personalityType);
        // Don't set personalityCompleted - let user go through quiz
      }
      
      // Preload expertise quiz result - but don't mark as completed
      if (initialDesignerAnswers.expertise) {
        setExpertiseQuizResult(initialDesignerAnswers.expertise);
        // Don't set expertiseCompleted - let user go through all 7 steps including rankings
        setExpertiseShowingBegin(false); // Skip the begin screen to start quiz
      }
      
      // Preload designer style quiz result - but don't mark as completed
      if (initialDesignerAnswers.style) {
        setDesignerStyleQuizResult(initialDesignerAnswers.style);
        // Don't set designerStyleCompleted - let user go through all steps
      }
      
      // Preload work style quiz result - but don't mark as completed
      if (initialDesignerAnswers.workStyle) {
        setWorkStyleQuizResult(initialDesignerAnswers.workStyle);
        // Don't set workStyleCompleted - let user go through all steps
        setWorkStyleShowingBegin(false); // Skip the begin screen to start quiz
      }
      
      // Preload preferences quiz result - but don't mark as completed
      if (initialDesignerAnswers.preferences) {
        setPreferenceQuizResult(initialDesignerAnswers.preferences);
        // Don't set preferencesCompleted - let user go through all steps
        setPreferencesShowingBegin(false); // Skip the begin screen to start quiz
        // Check if zip codes exist
        if (initialDesignerAnswers.preferences.zipCodes && initialDesignerAnswers.preferences.zipCodes.length > 0) {
          setHasZipCodes(true);
        }
      }
    }
  }, [initialDesignerAnswers, isRetakeInProgress]);

  // During retake, users should go through all steps normally
  // No need to maintain completion state - let them retake the quiz

  const [createHomeownerProfile] = useMutation(CREATE_HOMEOWNER_MATCHING_PROFILE);
  const [updateHomeownerProfile] = useMutation(UPDATE_HOMEOWNER_MATCHING_PROFILE);
  
  // Use the new custom hooks for designer mutations
  const { createProfile: createDesignerProfile, loading: createDesignerLoading } = useCreateDesignerMatchingProfileMutation();
  const { updateProfile: updateDesignerProfile, loading: updateDesignerLoading } = useUpdateDesignerMatchingProfileMutation();
  const { refreshOnboardingStatus } = useGetDesignerOnboardingStatusQuery(user?.id);
  
  // Personality Quiz state
  const [currentPersonalityStep, setCurrentPersonalityStep] = useState<number>(1);
  const [personalityTotalSteps, setPersonalityTotalSteps] = useState<number>(5);
  const [personalityCompleted, setPersonalityCompleted] = useState<boolean>(false);
  
  // Expertise Quiz state (designer step 2)
  const [currentExpertiseStep, setCurrentExpertiseStep] = useState<number>(1);
  const [expertiseTotalSteps, setExpertiseTotalSteps] = useState<number>(7);
  const [expertiseCompleted, setExpertiseCompleted] = useState<boolean>(false);
  const [expertiseShowingBegin, setExpertiseShowingBegin] = useState<boolean>(true);
  
  // Work Style Quiz state (designer step 4)
  const [currentWorkStyleStep, setCurrentWorkStyleStep] = useState<number>(1);
  const [workStyleTotalSteps, setWorkStyleTotalSteps] = useState<number>(4);
  const [workStyleCompleted, setWorkStyleCompleted] = useState<boolean>(false);
  const [workStyleShowingBegin, setWorkStyleShowingBegin] = useState<boolean>(true);

  // Ensure workStyleShowingBegin is false when user is on step > 1
  useEffect(() => {
    if (currentWorkStyleStep > 1) {
      setWorkStyleShowingBegin(false);
    }
  }, [currentWorkStyleStep]);

  
  // Preferences Quiz state (designer step 5)
  const [currentPreferencesStep, setCurrentPreferencesStep] = useState<number>(1);
  const [preferencesTotalSteps, setPreferencesTotalSteps] = useState<number>(5);
  const [preferencesCompleted, setPreferencesCompleted] = useState<boolean>(false);
  const [preferencesShowingBegin, setPreferencesShowingBegin] = useState<boolean>(true);
  const [hasZipCodes, setHasZipCodes] = useState<boolean>(false);

  const { setFooter, clearFooter } = useDashboardFooter();
  const goToYourHub = useCallback(() => {
    clearFooter();
    if (!isHomeowner && refreshOnboardingStatus && user?.id) {
      refreshOnboardingStatus(user.id)
      .catch((err) => console.error('Error refreshing onboarding status:', err))
        .finally(() => navigate(Pages.YOUR_HUB));
    } else {
      navigate(Pages.YOUR_HUB);
    }
  }, [clearFooter, isHomeowner, navigate, refreshOnboardingStatus, user?.id]);

  // State for storing quiz results from each step
  const [styleQuizResult, setStyleQuizResult] = useState<any>(null);
  const [preferenceQuizResult, setPreferenceQuizResult] = useState<any>(null);
  const [prioritiesQuizResult, setPrioritiesQuizResult] = useState<any>(null);
  const [projectBudgetRange, setProjectBudgetRange] = useState<string | null>(null);
  
  // Designer quiz result states
  const [expertiseQuizResult, setExpertiseQuizResult] = useState<any>(null);
  const [designerStyleQuizResult, setDesignerStyleQuizResult] = useState<any>(null);
  const [workStyleQuizResult, setWorkStyleQuizResult] = useState<any>(null);

  // Refs for quiz components
  const workStyleQuizRef = useRef<WorkStyleQuizRef>(null);

  const projectInitialData = useMemo(() => {
    if (!initialHomeownerAnswers?.project && !initialHomeownerAnswers?.preferences?.budgetRange && !projectBudgetRange) {
      return null;
    }

    return {
      ...(initialHomeownerAnswers?.project || {}),
      budgetRange: projectBudgetRange ?? initialHomeownerAnswers?.preferences?.budgetRange,
    };
  }, [
    initialHomeownerAnswers?.project,
    initialHomeownerAnswers?.preferences?.budgetRange,
    projectBudgetRange,
  ]);

  const homeownerPreferencesInitialData = useMemo(() => {
    if (!initialHomeownerAnswers?.preferences && !projectBudgetRange) {
      return null;
    }

    return {
      ...(initialHomeownerAnswers?.preferences || {}),
      budgetRange: projectBudgetRange ?? initialHomeownerAnswers?.preferences?.budgetRange,
    };
  }, [initialHomeownerAnswers?.preferences, projectBudgetRange]);

  const resetDesignerRetakeState = useCallback(() => {
    setPersonalityCompleted(false);
    setCurrentPersonalityStep(1);
    setPersonalityResult(null);
    setShowingBegin(true);

    setExpertiseCompleted(false);
    setCurrentExpertiseStep(1);
    setExpertiseTotalSteps(7);
    setExpertiseShowingBegin(true);
    setExpertiseQuizResult(null);

    setDesignerStyleCompleted(false);
    setCurrentDesignerStyleStep(1);
    setDesignerStyleQuizResult(null);

    setWorkStyleCompleted(false);
    setCurrentWorkStyleStep(1);
    setWorkStyleShowingBegin(true);
    setWorkStyleQuizResult(null);

    setPreferencesCompleted(false);
    setCurrentPreferencesStep(1);
    setPreferencesShowingBegin(true);
    setPreferenceQuizResult(null);
    setHasZipCodes(false);
  }, []);

  const resetHomeownerRetakeState = useCallback(() => {
    setPersonalityCompleted(false);
    setCurrentPersonalityStep(1);
    setPersonalityResult(null);

    setStyleQuizResult(null);
    setPreferenceQuizResult(null);
    setPrioritiesQuizResult(null);
    setProjectBudgetRange(null);
  }, []);
  
  // Function to check if work style quiz step is valid
  const isWorkStyleStepValid = useCallback(() => {
    if (!workStyleQuizResult) return false;
    
    // Step 1: Partnership options (requires at least one selection)
    if (currentWorkStyleStep === 1) {
      return workStyleQuizResult.partnershipOptions && workStyleQuizResult.partnershipOptions.length > 0;
    }
    
    // Step 2: Compensation models (requires at least one selection)
    if (currentWorkStyleStep === 2) {
      return workStyleQuizResult.compensationModel && workStyleQuizResult.compensationModel.length > 0;
    }
    
    // Step 3: Presentation types (requires at least one selection)
    if (currentWorkStyleStep === 3) {
      return workStyleQuizResult.presentationType && workStyleQuizResult.presentationType.length > 0;
    }
    
    // Step 4: Minimum budget (requires selection)
    if (currentWorkStyleStep === 4) {
      return workStyleQuizResult.minimumBudget && workStyleQuizResult.minimumBudget.trim() !== '';
    }
    
    return true;
  }, [currentWorkStyleStep, workStyleQuizResult]);
  
  // Designer style quiz state
  const [currentDesignerStyleStep, setCurrentDesignerStyleStep] = useState(1);
  const [designerStyleTotalSteps] = useState(4);
  const [designerStyleCompleted, setDesignerStyleCompleted] = useState(false);

  // Refs for quiz components
  const personalityQuizRef = useRef<PersonalityQuizRef>(null);
  const expertiseQuizRef = useRef<ExpertiseQuizRef>(null);
  const designerStyleQuizRef = useRef<DesignerStyleQuizRef>(null);
  const preferencesQuizRef = useRef<PreferencesQuizRef>(null);

  const startDesignerRetake = useCallback(() => {
    if (isHomeowner) return;

    clearFooter();
    setViewResultsDialogOpen(false);
    resetDesignerRetakeState();
    personalityQuizRef.current?.reset();
    setMainStep(1);
    setIsRetakeInProgress(true);
  }, [clearFooter, isHomeowner, personalityQuizRef, resetDesignerRetakeState, setIsRetakeInProgress, setMainStep, setViewResultsDialogOpen]);

  const startHomeownerRetake = useCallback(() => {
    if (!isHomeowner) return;

    clearFooter();
    setViewHomeownerResultsDialogOpen(false);
    resetHomeownerRetakeState();
    personalityQuizRef.current?.reset();
    setPersonalityCompleted(false);
    setPersonalityResult(null);
    setShowingBegin(true);
    setMainStep(1);
    setIsHomeownerRetakeInProgress(true);
  }, [
    clearFooter,
    isHomeowner,
    personalityQuizRef,
    resetHomeownerRetakeState,
    setIsHomeownerRetakeInProgress,
    setMainStep,
    setPersonalityCompleted,
    setPersonalityResult,
    setShowingBegin,
    setViewHomeownerResultsDialogOpen,
  ]);

  const handlePersonalityRetake = useCallback(() => {
    if (isHomeowner) {
      startHomeownerRetake();
      return;
    }

    startDesignerRetake();
  }, [isHomeowner, startDesignerRetake, startHomeownerRetake]);

  // Function to check if expertise quiz step requires selections and if they've been made
  const isExpertiseStepValid = useCallback(() => {
    if (!expertiseQuizResult) return false;
    
    // Step 1: Project types selection (requires at least one selection)
    if (currentExpertiseStep === 1) {
      return expertiseQuizResult.projectTypes && expertiseQuizResult.projectTypes.length > 0;
    }
    
    // Step 3: Experience types selection (requires at least one selection)
    if (currentExpertiseStep === 3) {
      return expertiseQuizResult.experience && expertiseQuizResult.experience.length > 0;
    }
    
    // Step 5: Competency selection (requires all 14 competency areas to be rated)
    if (currentExpertiseStep === 5) {
      if (!expertiseQuizResult.competency) {
        return false;
      }
      const competencyAreas = [
        'accessibility', 'petFriendly', 'smartHome', 'customStorage', 
        'specificAcoustic', 'environmentallySustainable', 'uniqueLightingEffects',
        'historicalPreservation', 'outdoorLiving', 'specializedVentilation', 
        'fengShui', 'artIntegration', 'wineCellar', 'homeGym'
      ];
      const isValid = competencyAreas.every(area => {
        const hasValue = expertiseQuizResult.competency[area] && 
                        expertiseQuizResult.competency[area] !== undefined;
        return hasValue;
      });
      return isValid;
    }
    
    // Step 6: Strengths selection (requires at least one selection)
    // NOTE: Step 6 is the selection step, not the ranking step
    if (currentExpertiseStep === 6) {
      return expertiseQuizResult.strengths && expertiseQuizResult.strengths.length > 0;
    }
    
    // Step 7: Strengths ranking (requires all selected strengths to be ranked)
    // NOTE: Step 7 is the ranking step
    if (currentExpertiseStep === 7) {
      if (!expertiseQuizResult.strengths || expertiseQuizResult.strengths.length === 0) {
        return false;
      }
      // Check if all selected strengths have been ranked
      const rankedStrengths = expertiseQuizResult.strengths || [];
      const isValid = rankedStrengths.length > 0;
      return isValid;
    }
    
    // Steps 2, 4 are ranking steps - they don't require additional selections
    // as they work with previously selected items
    return true;
  }, [currentExpertiseStep, expertiseQuizResult]);

  // Function to check if designer style quiz step is valid
  const isDesignerStyleStepValid = useCallback(() => {
    if (!designerStyleQuizResult) return false;
    
    // Step 1: Can design any style (requires selection)
    if (currentDesignerStyleStep === 1) {
      return designerStyleQuizResult.canDesignAnyStyle !== null && designerStyleQuizResult.canDesignAnyStyle !== undefined;
    }
    
    // Step 2: Has known style (requires selection, and if Yes, requires a style selection)
    if (currentDesignerStyleStep === 2) {
      const hasKnownStyleAnswered = designerStyleQuizResult.hasKnownStyle !== null && designerStyleQuizResult.hasKnownStyle !== undefined;
      if (!hasKnownStyleAnswered) return false;
      
      // If they said Yes, they must also select a style
      if (designerStyleQuizResult.hasKnownStyle === true) {
        return designerStyleQuizResult.knownStyle && designerStyleQuizResult.knownStyle.length > 0;
      }
      
      return true;
    }
    
    // Step 3: Expertise styles (requires at least one selection)
    if (currentDesignerStyleStep === 3) {
      return designerStyleQuizResult.expertiseStyles && designerStyleQuizResult.expertiseStyles.length > 0;
    }
    
    // Step 4: Ranking (always valid - just needs to exist)
    if (currentDesignerStyleStep === 4) {
      return designerStyleQuizResult.styleRankings && designerStyleQuizResult.styleRankings.length > 0;
    }
    
    return true;
  }, [currentDesignerStyleStep, designerStyleQuizResult]);

  // Function to save personality quiz data
  const savePersonalityData = useCallback(async () => {
    if (!personalityResult || !user?.id) {
      return;
    }
    
    
    try {
      if (isHomeowner) {
        // Use ref to preserve existing step completion state
        const stepCompletionUpdate = { personalityCompleted: true };
        const updatedStepCompletion = normalizeHomeownerStepCompletion({
          ...homeownerStepCompletionRef.current,
          ...stepCompletionUpdate,
        });

        if (profileId) {
          // Get existing profile data to preserve all previous answers
          const existingProfile = homeownerProfileData?.getHomeOwnerMatchingProfile || 
                                 homeownerStepStatusData?.getHomeownerStepStatus?.profile;
          
          // Clean GraphQL query results by removing __typename fields
          const cleanHomeOwnerStyles = existingProfile?.homeOwnerStyles ? removeTypename(existingProfile.homeOwnerStyles) : undefined;
          const cleanProjectType = existingProfile?.projectType ? removeTypename(existingProfile.projectType) : undefined;
          const cleanPreferences = existingProfile?.preferences ? removeTypename(existingProfile.preferences) : undefined;
          const cleanPriorities = existingProfile?.priorities ? removeTypename(existingProfile.priorities) : undefined;
          
          // Update existing homeowner profile - include ALL previous data
          await updateHomeownerProfile({
            variables: {
              _id: profileId,
              personalityType: personalityResult, // Single string value
              homeOwnerStyles: cleanHomeOwnerStyles, // Preserve style (cleaned)
              projectType: cleanProjectType, // Preserve project data (cleaned)
              preferences: cleanPreferences, // Preserve preferences (cleaned)
              priorities: cleanPriorities, // Preserve priorities (cleaned)
              stepCompletion: stepCompletionUpdate
            }
          });
          syncHomeownerStepCompletion(updatedStepCompletion);
          // Refetch profile data to get latest for next step
          if (hasPreviousHomeownerProfileForQuery) {
            await refetchHomeownerProfile();
          }
        } else {
          // Create new homeowner profile
          const initialStepCompletion: HomeownerStepCompletionState = {
            ...createEmptyHomeownerStepCompletion(),
            personalityCompleted: true,
          };

          const result = await createHomeownerProfile({
            variables: {
              homeOwnerId: user.id,
              personalityType: personalityResult, // Single string value
              stepCompletion: initialStepCompletion
            }
          });
          syncHomeownerStepCompletion(initialStepCompletion);
          
          // After creation, refetch to get the actual MongoDB _id
          const refetchResult = await refetchHomeownerStepStatus();
          if (refetchResult.data?.getHomeownerStepStatus?.profile?._id) {
            setProfileId(refetchResult.data.getHomeownerStepStatus.profile._id);
          }
        }
        // Refetch homeowner step status
        await refetchHomeownerStepStatus();
      } else {
        // Designer flow
        if (profileId) {
          // Update existing designer profile
          const updatedStepCompletion: DesignerStepCompletionState = {
            ...designerStepCompletionRef.current,
            personalityCompleted: true,
          };

          await updateDesignerProfile({
            _id: profileId,
            personalityType: personalityResult, // Single string value
            stepCompletion: updatedStepCompletion
          });
          syncDesignerStepCompletion(updatedStepCompletion);
        } else {
          // Create new designer profile
          const initialStepCompletion: DesignerStepCompletionState = {
            ...createEmptyDesignerStepCompletion(),
            personalityCompleted: true,
          };

          await createDesignerProfile({
            designerId: user.id,
            personalityType: personalityResult, // Single string value
            stepCompletion: initialStepCompletion
          });
          syncDesignerStepCompletion(initialStepCompletion);
          
          // After creation, refetch to get the actual MongoDB _id
          const refetchResult = await refetchDesignerStepStatus();
          if (refetchResult.data?.getDesignerStepStatus?.profile?._id) {
            setProfileId(refetchResult.data.getDesignerStepStatus.profile._id);
          }
        }
        // Refetch designer step status
        await refetchDesignerStepStatus();
        await refreshDesignerProfile();
      }
    } catch (error) {
      console.error('Error saving personality data:', error);
    }
  }, [
    personalityResult,
    user?.id,
    profileId,
    isHomeowner,
    updateHomeownerProfile,
    createHomeownerProfile,
    refetchHomeownerStepStatus,
    updateDesignerProfile,
    createDesignerProfile,
    refetchDesignerStepStatus,
    syncDesignerStepCompletion,
    refreshDesignerProfile,
    syncHomeownerStepCompletion
  ]);


  
  const ContinueBar = ({ onBack, onNext, label, disabled }: { onBack?: () => void; onNext?: () => void; label?: string; disabled?: boolean }) => {
    useEffect(() => {
      setFooter(
        <>
          <Button 
            onClick={onBack} 
            variant="text" 
            startIcon={<ChevronLeft />} 
            disabled={!onBack}
            sx={{ fontSize: { xs: '14px', sm: '16px' } }}
          >
            Back
          </Button>
          <Button 
            onClick={onNext} 
            variant="contained" 
            endIcon={<ChevronRight />} 
            sx={{ 
              px: 4,
              fontSize: { xs: '14px', sm: '16px' }
            }} 
            disabled={disabled}
          >
            {label}
          </Button>
        </>
      );
      return () => clearFooter();
    }, [onBack, onNext, label, disabled, setFooter, clearFooter]);
    return null;
  };



  // Handle step status data to determine starting step
  useEffect(() => {
    if (isHomeowner) {
      
      if (homeownerStepStatusData?.getHomeownerStepStatus) {
        const { currentStep, profileExists, profile } = homeownerStepStatusData.getHomeownerStepStatus;
        
        const stepCompletion = normalizeHomeownerStepCompletion(profile?.stepCompletion);
        const profileComplete = isHomeownerProfileCompleted(stepCompletion);
        
        if (!isHomeownerRetakeInProgress) {
          setMainStep(profileComplete ? 1 : mapHomeownerCurrentStep(currentStep));
        }
        
        if (profileExists && profile) {
          setProfileId(profile._id); // Store MongoDB _id for updates
        } else {
        }
      } else if (!shouldFetchHomeownerStepStatus) {
        // If we shouldn't fetch (not a homeowner or no user ID), default to step 1
        setMainStep(1);
      } else if (homeownerStepStatusError) {
        // If there's an error, still allow user to proceed (they can start fresh)
        setMainStep(1);
      } else if (!homeownerStepStatusLoading && !homeownerStepStatusData) {
        // If query completed but no data, allow user to start fresh
        setMainStep(1);
      }
    } else {
      // Designer flow
      if (designerStepStatusData) {
        const { currentStep, profileExists, profile } = designerStepStatusData;

        const stepCompletion = profile?.stepCompletion;
        const profileComplete = !!(
          stepCompletion?.personalityCompleted &&
          stepCompletion?.expertiseCompleted &&
          stepCompletion?.styleCompleted &&
          stepCompletion?.workStyleCompleted &&
          stepCompletion?.preferencesCompleted
        );

        if (!isRetakeInProgress) {
          // If we've just finished (mainStep === 6), keep showing success; otherwise show the normal entry point
          if (mainStep !== 6) {
            setMainStep(profileComplete ? 1 : currentStep);
          }
        }

        if (profileExists && profile) {
          setProfileId(profile._id); // Store MongoDB _id for updates
        } else {
        }
      } else if (!shouldFetchDesignerStepStatus || designerStepStatusError) {
        // Fallback to step 1 if we can't fetch or there's an error
        setMainStep(1);
      }
    }
  }, [
    homeownerStepStatusData,
    designerStepStatusData,
    isHomeowner,
    shouldFetchHomeownerStepStatus,
    shouldFetchDesignerStepStatus,
    homeownerStepStatusError,
    designerStepStatusError,
    isRetakeInProgress,
    isHomeownerRetakeInProgress
  ]);

  // Clear footer when showing PersonalityQuiz begin screen only
  // Other components manage their own Begin states via their FixedBottomBar or ContinueBar logic
  useEffect(() => {
    if (
      showingBegin &&
      !showDesignerPersonalityResultLanding &&
      !showHomeownerPersonalityResultLanding
    ) {
      clearFooter();
    }
  }, [showingBegin, clearFooter, showDesignerPersonalityResultLanding, showHomeownerPersonalityResultLanding]);

  useEffect(() => {
    if (mainStep === 6) clearFooter();
  }, [mainStep, clearFooter]);

  // Ensure the landing screen (completed profile revisit) shows the footer controls
  useEffect(() => {
    if (showDesignerPersonalityResultLanding) {
      setShowingBegin(false);
      setPersonalityCompleted(true);
      if (!personalityResult && designerPersonalityResult) {
        setPersonalityResult(designerPersonalityResult);
      }
      return;
    }

    if (showHomeownerPersonalityResultLanding) {
      setShowingBegin(false);
      setPersonalityCompleted(true);
      if (!personalityResult && homeownerPersonalityResult) {
        setPersonalityResult(homeownerPersonalityResult);
      }
    }
  }, [
    showDesignerPersonalityResultLanding,
    showHomeownerPersonalityResultLanding,
    designerPersonalityResult,
    homeownerPersonalityResult,
    personalityResult,
  ]);

  const shouldHidePersonalityFooter =
    showingBegin &&
    !showDesignerPersonalityResultLanding &&
    !showHomeownerPersonalityResultLanding;

  // Ensure footer is visible when navigating to Work Style step (mainStep 4)
  // Reset showingBegin state if user is returning to this step (not starting fresh)
  useEffect(() => {
    if (mainStep === 4 && workStyleShowingBegin) {
      // If we have existing data (from quiz result or initial data), auto-begin so all steps are available
      if (workStyleQuizResult || initialDesignerAnswers?.workStyle) {
        setWorkStyleShowingBegin(false);
        workStyleQuizRef.current?.handleBegin?.();
      }
    }
  }, [mainStep, workStyleQuizResult, initialDesignerAnswers?.workStyle, workStyleShowingBegin]);

  // Ensure footer is visible when navigating to Preferences step (mainStep 5)
  // Reset showingBegin state if user is returning to this step (not starting fresh)
  useEffect(() => {
    if (mainStep === 5) {
      // If we have existing data (from quiz result or initial data), user is returning - ensure footer is visible
      if (preferenceQuizResult || initialDesignerAnswers?.preferences) {
        setPreferencesShowingBegin(false);
      }
    }
  }, [mainStep, preferenceQuizResult, initialDesignerAnswers?.preferences]);

  // If the user clears all styles on step 3 during a retake, force completion flag off so the Continue button disables
  useEffect(() => {
    if (
      mainStep === 3 &&
      designerStyleCompleted &&
      currentDesignerStyleStep === 3 &&
      (!designerStyleQuizResult?.expertiseStyles || designerStyleQuizResult.expertiseStyles.length === 0)
    ) {
      setDesignerStyleCompleted(false);
    }
  }, [mainStep, currentDesignerStyleStep, designerStyleCompleted, designerStyleQuizResult?.expertiseStyles]);

  const personalityInitialResult = isHomeowner
    ? (showHomeownerPersonalityResultLanding
        ? homeownerPersonalityResult ?? null
        : shouldPrefillHomeownerAnswers
        ? initialHomeownerAnswers?.personalityType ?? null
        : null)
    : showDesignerPersonalityResultLanding
    ? designerPersonalityResult
    : null;

  const content = useMemo(() => {
    // Step 1: Personality (same for both homeowner and designer)
    if (mainStep === 1) {
      return (
        <>
          <PersonalityQuiz 
            ref={personalityQuizRef}
            initialResult={personalityInitialResult}
            onResultChange={setPersonalityResult}
            onBeginShown={() => setShowingBegin(true)}
            onQuizStarted={() => {
              setShowingBegin(false);
            }}
            onStepChange={(step, total) => {
              setCurrentPersonalityStep(step);
              setPersonalityTotalSteps(total);
            }}
            onComplete={() => setPersonalityCompleted(true)}
            beginLabel={
              isHomeowner
                ? (hasPreviousHomeownerProfile ? "Continue" : "Begin")
                : hasPreviousDesignerProfile
                ? "Re-take Matching Profile"
                : "Begin"
            }
            autoStartOnInitialResult={!isHomeowner || showHomeownerPersonalityResultLanding}
            prefillFromInitialResult={isHomeowner && shouldPrefillHomeownerAnswers}
            showSuperPowerCard
            contentVariant={isHomeowner ? "homeowner" : "designer"}
            onRetake={handlePersonalityRetake}
          />
          {(!showingBegin || showDesignerPersonalityResultLanding || showHomeownerPersonalityResultLanding) && !shouldHidePersonalityFooter && (
            <ContinueBar
              label={personalityCompleted ? 'Save and Continue' : `Continue ${currentPersonalityStep} of ${totalSteps}`}
              onBack={() => {
                if (personalityCompleted) {
                  // If completed (showing results), reset to go back to quiz questions
                  // Reset the quiz and clear completion state so user can retake
                  // Clear initialResult temporarily to prevent auto-prefill
                  setPersonalityCompleted(false);
                  setPersonalityResult(null);
                  personalityQuizRef.current?.reset();
                  // After reset, restore initialResult so user can see their previous answers
                  // But don't auto-complete - let them start fresh
                } else {
                  // Trigger the PersonalityQuiz's internal back step
                  personalityQuizRef.current?.backStep();
                }
              }}
              onNext={async () => {
                if (personalityCompleted) {
                  clearFooter(); // Clear PersonalityQuiz footer before transitioning
                  // Save personality data for both homeowners AND designers
                  await savePersonalityData();
                  setMainStep(2);
                } else if (isHomeowner && shouldPrefillHomeownerAnswers) {
                  personalityQuizRef.current?.advanceWithPrefill();
                }
                // If not completed, the Continue button doesn't do anything - PersonalityQuiz handles progression
              }}
            />
          )}
        </>
      );
    }

    // Different flows for homeowner vs designer
    if (isHomeowner) {
      // Homeowner Flow: Personality → Design Style → Project → Preferences → Priorities
      if (mainStep === 2) {
        return (
          <>
            <StyleQuiz 
              beginLabel={
                isHomeownerProfileComplete && !isHomeownerRetakeInProgress
                  ? "Continue"
                  : "Begin"
              }
              initialStyleValue={
                shouldPrefillHomeownerAnswers
                  ? initialHomeownerAnswers?.style?.selectedStyles?.[0] ??
                    initialHomeownerAnswers?.style?.homeOwnerStyle?.[0] ?? null
                  : null
              }
              initialStyleValues={
                shouldPrefillHomeownerAnswers
                  ? (initialHomeownerAnswers?.style?.selectedStyles?.length
                      ? initialHomeownerAnswers?.style?.selectedStyles
                      : initialHomeownerAnswers?.style?.homeOwnerStyle ?? null)
                  : null
              }
              initialFavoriteLook={
                shouldPrefillHomeownerAnswers
                  ? initialHomeownerAnswers?.style?.favoriteLook ?? null
                  : null
              }
              initialStyleDetail={
                shouldPrefillHomeownerAnswers
                  ? {
                      selectedStyles: initialHomeownerAnswers?.style?.selectedStyles ?? null,
                      selectedImages: initialHomeownerAnswers?.style?.selectedImages ?? null,
                      matchupResults: initialHomeownerAnswers?.style?.matchupResults ?? null,
                      finalSelections: initialHomeownerAnswers?.style?.finalSelections ?? null,
                    }
                  : null
              }
              onComplete={async (result) => {
                setStyleQuizResult(result);
                // Save style data directly with the result
                if (result && user?.id) {
                  try {
                    // Use ref to preserve existing step completion state
                    const stepCompletionUpdate = { styleCompleted: true };
                    const updatedStepCompletion = normalizeHomeownerStepCompletion({
                      ...homeownerStepCompletionRef.current,
                      ...stepCompletionUpdate,
                    });

                    const rawStyles =
                      result.type === 'combined' && result.style1 && result.style2
                        ? [result.style1, result.style2]
                        : [result.style];

                    const cleanStyleValues = rawStyles
                      .map((style) => style.replace(/[^a-zA-Z]/g, ''))
                      .filter((style) => Boolean(style));

                    const normalizeStyleValue = (value?: string | null) => {
                      const cleaned = sanitizeStyleValue(value);
                      return cleaned ? cleaned : undefined;
                    };

                    const selectedStyles = (result.selectedStyles ?? [])
                      .map((style) => normalizeStyleValue(style))
                      .filter((style): style is string => Boolean(style));

                    const selectedImages = (result.selectedImages ?? [])
                      .map((item) => {
                        const style = normalizeStyleValue(item?.style ?? undefined);
                        if (!style) return null;
                        const imageIndex = Number(item?.imageIndex);
                        return {
                          style,
                          imageIndex: Number.isFinite(imageIndex) ? imageIndex : undefined,
                          src: item?.src ? String(item.src) : undefined,
                        };
                      })
                      .filter(Boolean) as Array<{ style: string; imageIndex?: number; src?: string }>;

                    const matchupResults = (result.matchupResults ?? [])
                      .map((item) => {
                        const style1 = normalizeStyleValue(item?.style1 ?? undefined);
                        const style2 = normalizeStyleValue(item?.style2 ?? undefined);
                        const winner = normalizeStyleValue(item?.winner ?? undefined);
                        if (!style1 || !style2 || !winner) return null;
                        return { style1, style2, winner };
                      })
                      .filter(Boolean) as Array<{ style1: string; style2: string; winner: string }>;

                    const finalSelections = (result.finalSelections ?? [])
                      .map((item) => {
                        const style = normalizeStyleValue(item?.style ?? undefined);
                        if (!style) return null;
                        const imageIndex = Number(item?.imageIndex);
                        return {
                          style,
                          imageIndex: Number.isFinite(imageIndex) ? imageIndex : undefined,
                          src: item?.src ? String(item.src) : undefined,
                        };
                      })
                      .filter(Boolean) as Array<{ style: string; imageIndex?: number; src?: string }>;

                    const favoriteLook =
                      selectedImages.length && Number.isFinite(selectedImages[0]?.imageIndex ?? null)
                        ? selectedImages[0].imageIndex
                        : 1;

                    if (profileId) {
                      // Get existing profile data to preserve all previous answers
                      const existingProfile = homeownerProfileData?.getHomeOwnerMatchingProfile || 
                                             homeownerStepStatusData?.getHomeownerStepStatus?.profile;
                      
                      // Clean GraphQL query results by removing __typename fields
                      let cleanProjectType = existingProfile?.projectType ? removeTypename(existingProfile.projectType) : undefined;
                      // CRITICAL: Fix conceptPresentation if it's null (should never be null, always array)
                      if (cleanProjectType && typeof cleanProjectType === 'object' && cleanProjectType !== null) {
                        const cp = (cleanProjectType as any).conceptPresentation;
                        if (cp === null || cp === undefined) {
                          console.warn('⚠️ cleanProjectType has null conceptPresentation, fixing to empty array');
                          cleanProjectType = {
                            ...cleanProjectType,
                            conceptPresentation: []
                          } as any;
                        }
                      }
                      const cleanPreferences = existingProfile?.preferences ? removeTypename(existingProfile.preferences) : undefined;
                      const cleanPriorities = existingProfile?.priorities ? removeTypename(existingProfile.priorities) : undefined;
                      const cleanHomeOwnerStyles = existingProfile?.homeOwnerStyles ? removeTypename(existingProfile.homeOwnerStyles) : undefined;
                      
                      // Update existing homeowner profile - include ALL previous data
                      await updateHomeownerProfile({
                        variables: {
                          _id: profileId,
                          homeOwnerStyles: {
                            homeOwnerStyle: cleanStyleValues,
                            favoriteLook,
                            selectedStyles: selectedStyles.length ? selectedStyles : undefined,
                            selectedImages: selectedImages.length ? selectedImages : undefined,
                            matchupResults: matchupResults.length ? matchupResults : undefined,
                            finalSelections: finalSelections.length ? finalSelections : undefined,
                          },
                          stepCompletion: stepCompletionUpdate
                        }
                      });
                      syncHomeownerStepCompletion(updatedStepCompletion);
                      // Refetch profile data to get latest for next step
                      if (hasPreviousHomeownerProfileForQuery) {
                        await refetchHomeownerProfile();
                      }
                    } else {
                      // Create new homeowner profile if it doesn't exist
                      const initialStepCompletion = normalizeHomeownerStepCompletion({
                        ...createEmptyHomeownerStepCompletion(),
                        ...homeownerStepCompletionRef.current,
                        styleCompleted: true,
                      });
                      const createResult = await createHomeownerProfile({
                        variables: {
                          homeOwnerId: user.id,
                          homeOwnerStyles: {
                            homeOwnerStyle: cleanStyleValues,
                            favoriteLook,
                            selectedStyles: selectedStyles.length ? selectedStyles : undefined,
                            selectedImages: selectedImages.length ? selectedImages : undefined,
                            matchupResults: matchupResults.length ? matchupResults : undefined,
                            finalSelections: finalSelections.length ? finalSelections : undefined,
                          },
                          stepCompletion: initialStepCompletion
                        }
                      });
                      syncHomeownerStepCompletion(initialStepCompletion);
                      
                      // After creation, refetch to get the actual MongoDB _id
                      const refetchResult = await refetchHomeownerStepStatus();
                      if (refetchResult.data?.getHomeownerStepStatus?.profile?._id) {
                        setProfileId(refetchResult.data.getHomeownerStepStatus.profile._id);
                      }
                    }
                    await refetchHomeownerStepStatus();
                  } catch (error) {
                  }
                }
                setMainStep(3);
              }}
              onBack={() => {
                // Navigate back to Personality step (mainStep 1)
                setMainStep(1);
              }}
            />
          </>
        );
      }
      if (mainStep === 3) {
        return (
          <>
            <ProjectQuiz
              initialData={projectInitialData}
              onBack={() => setMainStep(2)}
              onComplete={async (result) => {
                if (user?.id) {
                  try {
                    setProjectBudgetRange(result.budgetRange || null);
                    const stepCompletionUpdate = { projectCompleted: true };
                    const updatedStepCompletion = normalizeHomeownerStepCompletion({
                      ...homeownerStepCompletionRef.current,
                      ...stepCompletionUpdate,
                    });

                    const address = result.projectAddress;
                    const hasAddress =
                      !!address &&
                      Boolean(
                        address.streetAddress1 ||
                          address.city ||
                          address.state ||
                          address.zipCode
                      );

                    const projectPayload = {
                      projectType: result.projectType,
                      designItems: result.designItems || [],
                      specialNeeds: result.specialNeeds || [],
                      conceptPresentation: result.conceptPresentation || [],
                      projectAddress: hasAddress
                        ? {
                            id: address?.id,
                            streetAddress1: address?.streetAddress1 || "",
                            streetAddress2: address?.streetAddress2 || "",
                            city: address?.city || "",
                            state: address?.state || "",
                            zipCode: address?.zipCode || "",
                          }
                        : null,
                    };
                    const existingPreferences = homeownerProfileData?.getHomeOwnerMatchingProfile?.preferences
                      ? (removeTypename(
                          homeownerProfileData.getHomeOwnerMatchingProfile.preferences
                        ) as Record<string, unknown>)
                      : undefined;
                    const preferencesPayload = result.budgetRange
                      ? { ...(existingPreferences ?? {}), budgetRange: result.budgetRange }
                      : undefined;

                    if (profileId) {
                      await updateHomeownerProfile({
                        variables: {
                          _id: profileId,
                          projectType: projectPayload,
                          ...(preferencesPayload ? { preferences: preferencesPayload } : {}),
                          stepCompletion: stepCompletionUpdate,
                        },
                      });
                      syncHomeownerStepCompletion(updatedStepCompletion);
                      if (hasPreviousHomeownerProfileForQuery) {
                        await refetchHomeownerProfile();
                      }
                    } else {
                      const initialStepCompletion = normalizeHomeownerStepCompletion({
                        ...createEmptyHomeownerStepCompletion(),
                        ...homeownerStepCompletionRef.current,
                        projectCompleted: true,
                      });
                      await createHomeownerProfile({
                        variables: {
                          homeOwnerId: user.id,
                          projectType: projectPayload,
                          ...(preferencesPayload ? { preferences: preferencesPayload } : {}),
                          stepCompletion: initialStepCompletion,
                        },
                      });
                      syncHomeownerStepCompletion(initialStepCompletion);

                      const refetchResult = await refetchHomeownerStepStatus();
                      if (refetchResult.data?.getHomeownerStepStatus?.profile?._id) {
                        setProfileId(refetchResult.data.getHomeownerStepStatus.profile._id);
                      }
                    }

                    await refetchHomeownerStepStatus();
                  } catch (error) {}
                }
                setMainStep(4);
              }}
            />
          </>
        );
      }
      if (mainStep === 4) {
        return (
          <>
            <HomeownerPreferencesQuiz
              initialData={homeownerPreferencesInitialData}
              onComplete={async (result) => {
                // Save homeowner preferences
                if (user?.id) {
                  try {
                    const existingPriorityHireLikelihood =
                      (homeownerProfileData?.getHomeOwnerMatchingProfile?.preferences
                        ?.hireLikelihood ??
                        initialHomeownerAnswers?.preferences?.hireLikelihood ??
                        null) as string | null;
                    const preferencesPayload =
                      isPriorityHireLikelihoodValue(existingPriorityHireLikelihood) &&
                      (result as any)?.hireLikelihood == null
                        ? {
                            ...result,
                            hireLikelihood: existingPriorityHireLikelihood,
                          }
                        : result;

                    // Use ref to preserve existing step completion state
                    const stepCompletionUpdate = { preferenceCompleted: true };
                    const updatedStepCompletion = normalizeHomeownerStepCompletion({
                      ...homeownerStepCompletionRef.current,
                      ...stepCompletionUpdate,
                    });

                    if (profileId) {
                      // Update existing homeowner profile - ONLY send fields being modified
                      // The backend preserves existing data for fields not provided
                      // This prevents stale cache data from overwriting previously saved data
                      console.log(
                        "🟢 Saving preference step completion (update existing):",
                        updatedStepCompletion
                      );
                      await updateHomeownerProfile({
                        variables: {
                          _id: profileId,
                          preferences: preferencesPayload, // preserve priorities Q1 value
                          stepCompletion: stepCompletionUpdate,
                        },
                      });
                      syncHomeownerStepCompletion(updatedStepCompletion);
                      // Refetch profile data to get latest for next step
                      if (hasPreviousHomeownerProfileForQuery) {
                        await refetchHomeownerProfile();
                      }
                    } else {
                      // Create new homeowner profile if it doesn't exist
                      const initialStepCompletion = normalizeHomeownerStepCompletion({
                        ...createEmptyHomeownerStepCompletion(),
                        ...homeownerStepCompletionRef.current,
                        preferenceCompleted: true,
                      });
                      const createResult = await createHomeownerProfile({
                        variables: {
                          homeOwnerId: user.id,
                          preferences: preferencesPayload,
                          stepCompletion: initialStepCompletion,
                        },
                      });
                      syncHomeownerStepCompletion(initialStepCompletion);

                      // After creation, refetch to get the actual MongoDB _id
                      const refetchResult = await refetchHomeownerStepStatus();
                      if (refetchResult.data?.getHomeownerStepStatus?.profile?._id) {
                        setProfileId(refetchResult.data.getHomeownerStepStatus.profile._id);
                      }
                    }

                    setPreferenceQuizResult(preferencesPayload);
                    await refetchHomeownerStepStatus();
                    setMainStep(5);
                  } catch (error) {}
                }
              }}
              onBack={() => setMainStep(3)}
            />
          </>
        );
      }
      if (mainStep === 5) {
        const homeownerProfile =
          homeownerStepStatusData?.getHomeownerStepStatus?.profile;

        // Get priorities from the most complete data source
        const prioritiesFromFullProfile = homeownerProfileData?.getHomeOwnerMatchingProfile?.priorities?.order;
        const prioritiesFromStepStatus = homeownerProfile?.priorities?.order;
        const prioritiesFromInitialAnswers = initialHomeownerAnswers?.priorities;
        const initialOrder = prioritiesFromFullProfile || prioritiesFromStepStatus || prioritiesFromInitialAnswers || [];
        const preferencesFromFullProfile =
          homeownerProfileData?.getHomeOwnerMatchingProfile?.preferences;
        const preferencesFromInitialAnswers = initialHomeownerAnswers?.preferences ?? null;
        const existingPreferences =
          (preferencesFromFullProfile ??
            preferenceQuizResult ??
            preferencesFromInitialAnswers) as Record<string, unknown> | null;
        const initialHireLikelihood =
          (homeownerProfileData?.getHomeOwnerMatchingProfile?.preferences
            ?.hireLikelihood ??
            preferenceQuizResult?.hireLikelihood ??
            initialHomeownerAnswers?.preferences?.hireLikelihood ??
            null) as string | null;
        
        return (
          <>
            <HomeownerPriorities
              key={`priorities-${profileId || 'new'}`}
              profileId={profileId || ""}
              initialOrder={initialOrder}
              initialHireLikelihood={initialHireLikelihood}
              existingPreferences={existingPreferences}
              onBack={() => setMainStep(4)}
              onDone={async () => {
                // Update step completion ref after priorities are saved
                const updatedStepCompletion = normalizeHomeownerStepCompletion({
                  ...homeownerStepCompletionRef.current,
                  prioritiesCompleted: true,
                });
                
                // Priorities are ALREADY saved by HomeownerPriorities component
                // We only need to update the stepCompletion flag here
                // The backend preserves existing data for fields not provided
                // This prevents stale cache data from overwriting previously saved data
                if (profileId) {
                  try {
                    console.log('🟢 Saving priorities step completion (update existing):', updatedStepCompletion);
                    await updateHomeownerProfile({
                      variables: {
                        _id: profileId,
                        stepCompletion: {
                          prioritiesCompleted: true,
                        },
                      },
                    });
                  } catch (error) {
                  }
                }
                
                syncHomeownerStepCompletion(updatedStepCompletion);
                if (hasPreviousHomeownerProfileForQuery) {
                  await refetchHomeownerProfile();
                }
                await refetchHomeownerStepStatus();
                clearFooter();
                setMainStep(6);
              }}
            />
          </>
        );
      }

      if (mainStep === 6) {
        return (
          <MatchingProfileSuccess
            buttonLabel="Add a Project"
            onContinue={() => {
              navigate(Pages.MY_PROJECTS);
            }}
          />
        );
      }
    } else {
      // Designer Flow: Personality → Expertise → Design Style → Work Style → Preferences
      if (mainStep === 2) {
        return (
          <>
            <ExpertiseQuiz 
              ref={expertiseQuizRef}
              initialData={(() => {
                const expertiseData = initialDesignerAnswers?.expertise || null;
                return expertiseData;
              })()}
              onBeginShown={() => setExpertiseShowingBegin(true)}
              onQuizStarted={() => setExpertiseShowingBegin(false)}
              onStepChange={(step, total) => {
                setCurrentExpertiseStep(step);
                setExpertiseTotalSteps(total);
              }}
              onResultChange={setExpertiseQuizResult}
              onComplete={async () => {
                setExpertiseCompleted(true);
                // Save expertise data for designers
                if (user?.id && profileId) {
                  try {
                    // Get the latest result directly from the quiz component to ensure we have the most up-to-date data
                    const latestResult = expertiseQuizRef.current?.getCurrentResult();
                    
                    if (!latestResult) {
                      return;
                    }
                    
                    const updatedStepCompletion: DesignerStepCompletionState = {
                      ...designerStepCompletionRef.current,
                      expertiseCompleted: true,
                    };
                    
                    const mappedCompetency = mapCompetencyForGraphQL(
                      latestResult.competency || (latestResult as any).compentency
                    );
                    
                    // Fix: strengths should be inside competency object to match backend schema
                    const expertiseData = {
                      experienceRankings: latestResult.experienceRankings || [],
                      projectTypes: latestResult.projectTypes || [],
                      projectRankings: latestResult.projectRankings || [],
                      experience: latestResult.experience || [],
                      // GraphQL input uses typo `compentency`
                      compentency: {
                        ...mappedCompetency
                      },
                      // Strengths belong at the top level per schema
                      strengths: latestResult.strengths || []
                    };
                    
                    const result = await updateDesignerProfile({
                      _id: profileId,
                      expertise: expertiseData,
                      stepCompletion: updatedStepCompletion
                    });
                    
                    syncDesignerStepCompletion(updatedStepCompletion);
                    await refetchDesignerStepStatus();
                    await refreshDesignerProfile();
                  } catch (error) {
                  }
                } else {
                }
                setMainStep(3);
              }}
            />
            <ContinueBar
              label={
                expertiseShowingBegin
                  ? 'Begin'
                  : expertiseCompleted 
                    ? 'Save and Continue' 
                    : currentExpertiseStep === 7 
                      ? 'Save and Continue' 
                      : `Continue ${currentExpertiseStep} of ${expertiseTotalSteps}`
              }
              disabled={!expertiseShowingBegin && !expertiseCompleted && (!isExpertiseStepValid() || !profileId)}
              onBack={() => {
                if (expertiseShowingBegin || currentExpertiseStep === 1) {
                  // If on Begin screen OR first step of Expertise quiz, go back to Personality (mainStep 1)
                  setMainStep(1);
                } else {
                  // Trigger the ExpertiseQuiz's internal back step
                  expertiseQuizRef.current?.backStep();
                }
              }}
              onNext={async () => {
                if (expertiseShowingBegin) {
                  // Start the quiz by triggering Begin button
                  expertiseQuizRef.current?.handleBegin();
                  return;
                } else if (expertiseCompleted) {
                  setMainStep(3);
                } else if (currentExpertiseStep === 7) {
                  // On final step, complete the quiz
                  setExpertiseCompleted(true);
                  // Trigger the onComplete callback to save data
                  if (user?.id && profileId) {
                    try {
                      // Get the latest result directly from the quiz component to ensure we have the most up-to-date data
                      const latestResult = expertiseQuizRef.current?.getCurrentResult();
                      
                      if (!latestResult) {
                        return;
                      }
                      
                      const updatedStepCompletion: DesignerStepCompletionState = {
                        ...designerStepCompletionRef.current,
                        expertiseCompleted: true,
                      };
                      
                      const mappedCompetency = mapCompetencyForGraphQL(
                        latestResult.competency || (latestResult as any).compentency
                      );
                      
                      // Fix: strengths should be inside competency object to match backend schema
                      const expertiseData = {
                        experienceRankings: latestResult.experienceRankings || [],
                        projectTypes: latestResult.projectTypes || [],
                        projectRankings: latestResult.projectRankings || [],
                        experience: latestResult.experience || [],
                        compentency: {
                          ...mappedCompetency
                        },
                        strengths: latestResult.strengths || []
                      };
                      
                      await updateDesignerProfile({
                        _id: profileId,
                        expertise: expertiseData,
                        stepCompletion: updatedStepCompletion
                      });
                      syncDesignerStepCompletion(updatedStepCompletion);
                      await refetchDesignerStepStatus();
                      await refreshDesignerProfile();
                    } catch (error) {
                      // Don't proceed if save fails
                      return;
                    }
                  } else {
                    return;
                  }
                  setMainStep(3);
                } else {
                  // Check if current step is valid before proceeding
                  if (isExpertiseStepValid()) {
                    // Trigger the ExpertiseQuiz's internal next step
                    expertiseQuizRef.current?.nextStep();
                  }
                  // If not valid, do nothing - button should be disabled
                }
              }}
            />
          </>
        );
      }
      if (mainStep === 3) {
        return (
          <>
            <DesignerStyleQuiz 
              ref={designerStyleQuizRef}
              initialData={initialDesignerAnswers?.style || null}
              onStepChange={(step, total) => {
                setCurrentDesignerStyleStep(step);
              }}
              onResultChange={(result) => {
                setDesignerStyleQuizResult(result);
                // If user cleared styles while revisiting, ensure footer disables until valid again
                if (!result?.expertiseStyles || result.expertiseStyles.length === 0) {
                  setDesignerStyleCompleted(false);
                }
              }}
              onComplete={async (result) => {
                setDesignerStyleQuizResult(result);
                setDesignerStyleCompleted(true);
                // Save style data for designers
                if (result && user?.id && profileId) {
                  try {
                    const updatedStepCompletion: DesignerStepCompletionState = {
                      ...designerStepCompletionRef.current,
                      styleCompleted: true,
                    };

                    // Convert design styles to match backend format
                    const expertiseStyles = Array.isArray(result.expertiseStyles)
                      ? result.expertiseStyles
                      : [];
                    const cleanExpertiseStyles = expertiseStyles.map(style => 
                      style.replace(/[^a-zA-Z]/g, '')
                    );
                    
                    // Use styleRankings if available, otherwise use expertiseStyles order
                    const rankingsToUse = Array.isArray(result.styleRankings)
                      ? result.styleRankings
                      : expertiseStyles;
                    const cleanRankings = rankingsToUse.map(style => 
                      style.replace(/[^a-zA-Z]/g, '')
                    );
                    
                    const styleData = {
                      anyStyle: result.canDesignAnyStyle,
                      // Explicitly clear knownForStyle when the answer is No
                      knownForStyle: result.hasKnownStyle
                        ? result.knownStyle?.replace(/[^a-zA-Z]/g, '')
                        : null,
                      strongStyles: cleanExpertiseStyles,
                      styleRankings: cleanRankings.map((style, index) => ({ 
                        style, 
                        ranking: index + 1 
                      }))
                    };
                    
                    await updateDesignerProfile({
                      _id: profileId,
                      style: styleData,
                      stepCompletion: updatedStepCompletion
                    });
                    syncDesignerStepCompletion(updatedStepCompletion);
                    await refetchDesignerStepStatus();
                    await refreshDesignerProfile();
                  } catch (error) {
                  }
                } else {
                }
                setMainStep(4);
              }} 
            />
            <ContinueBar
              label={
                designerStyleCompleted 
                  ? 'Save and Continue' 
                  : currentDesignerStyleStep === 4
                    ? 'Save and Continue'
                    : `Continue ${currentDesignerStyleStep} of ${designerStyleTotalSteps}`
              }
              disabled={!designerStyleCompleted && (!isDesignerStyleStepValid() || !profileId)}
              onBack={() => {
                if (currentDesignerStyleStep === 1) {
                  // If on first step of Design Style quiz, go back to Expertise (mainStep 2)
                  // Ensure footer will be visible when navigating back
                  setMainStep(2);
                } else {
                  // Trigger the DesignerStyleQuiz's internal back step
                  designerStyleQuizRef.current?.backStep();
                }
              }}
              onNext={async () => {
                if (designerStyleCompleted) {
                  setMainStep(4);
                } else if (currentDesignerStyleStep === 4) {
                  // On step 4 (ranking), save data and move to next main step
                  setDesignerStyleCompleted(true);
                  
                  // Save style data for designers
                  if (designerStyleQuizResult && user?.id && profileId) {
                    try {
                      const updatedStepCompletion: DesignerStepCompletionState = {
                        ...designerStepCompletionRef.current,
                        styleCompleted: true,
                      };

                      // Convert design styles to match backend format
                      const expertiseStyles = Array.isArray(designerStyleQuizResult.expertiseStyles)
                        ? designerStyleQuizResult.expertiseStyles
                        : [];
                      const cleanExpertiseStyles = expertiseStyles.map((style: string) => 
                        style.replace(/[^a-zA-Z]/g, '')
                      );
                      
                      // Use styleRankings if available, otherwise use expertiseStyles order
                      const rankingsToUse = Array.isArray(designerStyleQuizResult.styleRankings)
                        ? designerStyleQuizResult.styleRankings
                        : expertiseStyles;
                      const cleanRankings = rankingsToUse.map((style: string) => 
                        style.replace(/[^a-zA-Z]/g, '')
                      );
                      
                      const styleData = {
                        anyStyle: designerStyleQuizResult.canDesignAnyStyle,
                        // Explicitly clear knownForStyle when answer is No
                        knownForStyle: designerStyleQuizResult.hasKnownStyle
                          ? designerStyleQuizResult.knownStyle?.replace(/[^a-zA-Z]/g, '')
                          : null,
                        strongStyles: cleanExpertiseStyles,
                        styleRankings: cleanRankings.map((style: string, index: number) => ({ 
                          style, 
                          ranking: index + 1 
                        }))
                      };
                      
                      await updateDesignerProfile({
                        _id: profileId,
                        style: styleData,
                        stepCompletion: updatedStepCompletion
                      });
                      syncDesignerStepCompletion(updatedStepCompletion);
                      await refetchDesignerStepStatus();
                      await refreshDesignerProfile();
                    } catch (error) {
                    }
                  } else {
                  }
                  
                  setMainStep(4);
                } else {
                  // Check if current step is valid before proceeding
                  if (isDesignerStyleStepValid()) {
                    // Trigger the DesignerStyleQuiz's internal next step
                    designerStyleQuizRef.current?.nextStep();
                  }
                  // If not valid, do nothing - button should be disabled
                }
              }}
            />
          </>
        );
      }
      if (mainStep === 4) {
        return (
          <>
            <WorkStyleQuiz 
              ref={workStyleQuizRef}
              currentStep={currentWorkStyleStep}
              initialData={initialDesignerAnswers?.workStyle || null}
              onBeginShown={() => setWorkStyleShowingBegin(true)}
              onQuizStarted={() => setWorkStyleShowingBegin(false)}
              onStepChange={(step, total) => {
                setCurrentWorkStyleStep(step);
                setWorkStyleTotalSteps(total);
              }}
              onResultChange={setWorkStyleQuizResult}
              onComplete={() => {
                // This will be called when the user actually completes the quiz
                // The onNext handler will handle the saving and marking as complete
              }}
            />
            <ContinueBar
              label={
                workStyleShowingBegin
                  ? 'Begin'
                  : workStyleCompleted
                    ? 'Save and Continue'
                    : currentWorkStyleStep === workStyleTotalSteps 
                      ? 'Save and Continue' 
                      : `Continue ${currentWorkStyleStep} of ${workStyleTotalSteps}`
              }
              disabled={!workStyleShowingBegin && (!isWorkStyleStepValid() || !profileId)}
              onBack={() => {
                if (workStyleShowingBegin) {
                  // If showing begin screen, go back to Design Style (mainStep 3)
                  setWorkStyleShowingBegin(false);
                  setMainStep(3);
                } else if (currentWorkStyleStep === 1) {
                  // If on first step, go back to Design Style (mainStep 3)
                  setWorkStyleShowingBegin(false);
                  setMainStep(3);
                } else {
                  setCurrentWorkStyleStep(prev => prev - 1);
                }
              }}
              onNext={async () => {
                if (workStyleShowingBegin) {
                  // When showing begin screen, trigger the Begin action via ref
                  workStyleQuizRef.current?.handleBegin();
                  return;
                }
                if (workStyleCompleted || currentWorkStyleStep === workStyleTotalSteps) {
                    // On last step or if already completed, save data first, then move to next step
                    if (workStyleQuizResult && user?.id && profileId) {
                      try {
                        // Save work style data
                        const updatedStepCompletion: DesignerStepCompletionState = {
                          ...designerStepCompletionRef.current,
                          workStyleCompleted: true,
                        };
                        const workStyleData = {
                          partnershipOptions: workStyleQuizResult.partnershipOptions || [],
                          compensationModel: workStyleQuizResult.compensationModel || [],
                          presentationType: workStyleQuizResult.presentationType || [],
                          minimumBudget: workStyleQuizResult.minimumBudget || "MEDIUM"
                        };
                        await updateDesignerProfile({
                          _id: profileId,
                          workStyle: workStyleData,
                          stepCompletion: updatedStepCompletion
                        });
                        syncDesignerStepCompletion(updatedStepCompletion);
                        
                        // Only mark as completed after successful save
                        setWorkStyleCompleted(true);
                        
                        await refetchDesignerStepStatus();
                        await refreshDesignerProfile();
                        // Refresh onboarding status to update UI
                        if (user?.id && refreshOnboardingStatus) {
                          await refreshOnboardingStatus(user.id);
                        }
                        setMainStep(5); // Move to next main step (Preferences)
                      } catch (error) {
                        // Don't mark as completed if save failed
                        // Optionally, show an error message to the user
                      }
                    } else {
                    }
                  } else if (currentWorkStyleStep < workStyleTotalSteps) {
                    // Move to next step
                    setCurrentWorkStyleStep(prev => prev + 1);
                  }
                }}
              />
          </>
        );
      }
      if (mainStep === 5) {
        return (
          <>
            <PreferencesQuiz 
              ref={preferencesQuizRef}
              initialData={initialDesignerAnswers?.preferences || null}
              onBeginShown={() => setPreferencesShowingBegin(true)}
              onQuizStarted={() => setPreferencesShowingBegin(false)}
              onStepChange={(step, total) => {
                setCurrentPreferencesStep(step);
                setPreferencesTotalSteps(total);
              }}
              onResultChange={(result) => {
                setPreferenceQuizResult(result);
              }}
              onZipCodesChange={setHasZipCodes}
              onComplete={() => {
                // Just mark as completed - the onNext handler will handle the saving
                // setPreferencesCompleted(true); // Removed this line
              }}
            />
            <ContinueBar
              label={
                preferencesShowingBegin
                  ? 'Begin'
                  : preferencesCompleted || currentPreferencesStep === preferencesTotalSteps 
                    ? 'Complete Matching Profile' 
                    : `Continue ${currentPreferencesStep} of ${preferencesTotalSteps}`
              }
              disabled={
                preferencesShowingBegin
                  ? false
                  : (currentPreferencesStep === preferencesTotalSteps && !hasZipCodes) || !profileId
              }
              onBack={() => {
                if (preferencesShowingBegin) {
                  // If showing begin screen, go back to Work Style (mainStep 4)
                  setPreferencesShowingBegin(false);
                  setMainStep(4);
                } else if (currentPreferencesStep === 1) {
                  // If on first step, go back to Work Style (mainStep 4)
                  setPreferencesShowingBegin(false);
                  setMainStep(4);
                } else {
                  // Trigger the PreferencesQuiz's internal back step
                  preferencesQuizRef.current?.backStep();
                }
              }}
              onNext={async () => {
                if (preferencesShowingBegin) {
                  // When showing begin screen, trigger the Begin action via ref
                  preferencesQuizRef.current?.handleBegin();
                  return;
                }
                if (preferencesCompleted || currentPreferencesStep === preferencesTotalSteps) {
                    // On the last step, save preferences data FIRST, then advance to success step
                    
                    // Get the current result directly from the quiz component via ref
                    const currentResult = preferencesQuizRef.current?.getCurrentResult();
                    
                    if (user?.id && profileId && currentResult) {
                      try {
                    // Force all steps to completed when the final step is saved so onboarding can progress
                    const updatedStepCompletion: DesignerStepCompletionState = {
                      personalityCompleted: true,
                      expertiseCompleted: true,
                      styleCompleted: true,
                      workStyleCompleted: true,
                      preferencesCompleted: true,
                    };
                        // Use actual quiz results from ref
                        const preferencesData = {
                          social: {
                            ...currentResult.social,
                            pets: currentResult.social?.pets && currentResult.social.pets.length
                              ? currentResult.social.pets
                              : undefined,
                          },
                          partnershipStyleRankings: (currentResult.partnershipStyleRankings || []).map((ranking) => ({
                            partnershipStyle: partnershipStyleUiToBackend[ranking.partnershipStyle] || ranking.partnershipStyle,
                            ranking: ranking.ranking,
                          })),
                          acquisitionPreferences: currentResult.acquisitionPreferences || [],
                          acquisitionRankings: currentResult.acquisitionRankings || [],
                          ageRange: currentResult.ageRange || "thirtyFiveToFortyFive",
                          children: currentResult.children || false,
                          zipCodes: currentResult.zipCodes || []
                        };
                        
                        await updateDesignerProfile({
                          _id: profileId,
                          preferences: preferencesData,
                          stepCompletion: updatedStepCompletion
                        });
                        syncDesignerStepCompletion(updatedStepCompletion);
                        
                        // Issue 3 fix: Refresh profile data before showing summary
                        // This ensures the summary shows the latest saved data
                        // Refetch step status (this is the primary data source for previousDesignerProfile)
                        const stepStatusResult = await refetchDesignerStepStatus();
                        
                        // Note: refreshDesignerProfile is disabled since we use designerStepStatusData.profile as primary source
                        
                        // Refresh onboarding status to update UI
                        if (user?.id && refreshOnboardingStatus) {
                          await refreshOnboardingStatus(user.id);
                        }
                        
                        setPreferencesCompleted(true);

                        // Always go to success/summary page (mainStep 6)
                        // The success page will handle retake vs first-time differently
                        setMainStep(6);
                      } catch (error) {
                        // DON'T advance to success if save fails - show error to user
                        showError('Unable to save your matching profile. Please try again or contact support if the problem persists.');
                        // Stay on current step so user can retry
                        return;
                      }
                    } else {
                      showError('Unable to save your matching profile. Please refresh the page and try again.');
                      return;
                    }
                  } else {
                    // Trigger the PreferencesQuiz's internal next step
                    preferencesQuizRef.current?.nextStep();
                  }
                }}
              />
          </>
        );
      }

      if (mainStep === 6) {
        // Show summary for retake, success for first time
        if (isRetakeInProgress && !isHomeowner && previousDesignerProfile) {
          return (
            <>
              <Box sx={{ maxWidth: '900px', mx: 'auto', py: 4 }}>
                <Typography variant="h4" sx={{ mb: 2, textAlign: 'center' }}>
                  Matching Profile Updated
                </Typography>
                <Typography variant="body1" sx={{ mb: 4, textAlign: 'center', color: 'text.secondary' }}>
                  Your matching profile has been successfully updated. Here's a summary of your answers:
                </Typography>
                <DesignerMatchingProfileRetakeSummary
                  profile={previousDesignerProfile}
                  loading={designerProfileLoading || designerStepStatusLoading}
                  currentSection="all"
                />
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={goToYourHub}
                  >
                    Done
                  </Button>
                </Box>
              </Box>
            </>
          );
        }

        // Show summary for homeowner (both first-time completion and retake)
        if (isHomeowner && isHomeownerProfileComplete && homeownerProfileData?.getHomeOwnerMatchingProfile) {
          const isRetake = isHomeownerRetakeInProgress;
          return (
            <>
              <Box sx={{ maxWidth: '900px', mx: 'auto', py: 4 }}>
                <Typography variant="h4" sx={{ mb: 2, textAlign: 'center' }}>
                  {isRetake ? 'Matching Profile Updated' : 'Matching Profile Complete'}
                </Typography>
                <Typography variant="body1" sx={{ mb: 4, textAlign: 'center', color: 'text.secondary' }}>
                  {isRetake 
                    ? "Your matching profile has been successfully updated. Here's a summary of your answers:"
                    : "Congratulations! You've completed your matching profile. Here's a summary of your answers:"}
                </Typography>
                <HomeownerMatchingProfileRetakeSummary
                  profile={homeownerProfileData.getHomeOwnerMatchingProfile}
                  loading={homeownerProfileLoading || homeownerStepStatusLoading}
                  currentSection="all"
                />
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => {
                      if (isRetake) {
                        setIsHomeownerRetakeInProgress(false);
                        resetHomeownerRetakeState();
                        personalityQuizRef.current?.reset();
                        setShowingBegin(true);
                        setPersonalityResult(null);
                        setMainStep(1);
                        goToYourHub();
                      } else {
                        navigate(Pages.MY_DESIGN_FILES);
                      }
                    }}
                  >
                    {isRetake ? 'Done' : 'Go to My Design Files'}
                  </Button>
                </Box>
              </Box>
            </>
          );
        }

        return (
          <MatchingProfileSuccess
            buttonLabel="Go to Your Hub"
            autoContinueMs={4000}
            onContinue={goToYourHub}
          />
        );
      }
    }

    return <Box />; // Fallback
  }, [
    mainStep, 
    showingBegin, 
    shouldHidePersonalityFooter,
    personalityInitialResult,
    currentPersonalityStep, 
    personalityCompleted, 
    expertiseShowingBegin,
    currentExpertiseStep,
    expertiseTotalSteps,
    expertiseCompleted,
    workStyleShowingBegin,
    currentWorkStyleStep,
    workStyleTotalSteps,
    workStyleCompleted,
    preferencesShowingBegin,
    currentPreferencesStep,
    preferencesTotalSteps,
    preferencesCompleted,
    isHomeowner,
    ContinueBar,
    clearFooter,
    savePersonalityData,
    totalSteps,
    user?.id,
    profileId,
    homeownerStepStatusData,
    designerStepStatusData,
    updateHomeownerProfile,
    updateDesignerProfile,
    refetchHomeownerStepStatus,
    refetchDesignerStepStatus,
    syncDesignerStepCompletion,
    isDesignerProfileComplete,
    hasPreviousDesignerProfile,
    designerProfileLoading,
    previousDesignerProfile,
    isBioUnlocked,
    refreshOnboardingStatus,
    resetDesignerRetakeState,
    hasPreviousDesignerProfile,
    previousDesignerProfile,
    designerProfileLoading,
    designerStepStatusLoading,
    viewResultsDialogOpen,
    isHomeownerRetakeInProgress,
    isHomeownerProfileComplete,
    hasPreviousHomeownerProfile,
    homeownerProfileData,
    homeownerProfileLoading,
    homeownerStepStatusLoading,
    resetHomeownerRetakeState
  ]);

  // Show loading state while fetching step status
  // For homeowners, don't wait for profileId - allow them to start even if no profile exists yet
  const isLoading = (isHomeowner && shouldFetchHomeownerStepStatus && homeownerStepStatusLoading) ||
      (!isHomeowner && shouldFetchDesignerStepStatus && designerStepStatusLoading) ||
      (!isHomeowner && !profileId && designerStepStatusData && designerStepStatusData.profileExists);
  
  
  if (isLoading) {
    return (
      <Box>
        <DashboardHeader title="Matching Profile" />
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress sx={{ color: '#00A06D' }} />
        </Box>
      </Box>
    );
  }

  // Show error state if there's an error fetching step status
  if ((isHomeowner && shouldFetchHomeownerStepStatus && homeownerStepStatusError) ||
      (!isHomeowner && shouldFetchDesignerStepStatus && designerStepStatusError)) {
    return (
      <Box>
        <DashboardHeader title="Matching Profile" />
        <Box p={4} textAlign="center">
          <Typography color="error">
            Error loading matching profile status. Please try refreshing the page.
          </Typography>
        </Box>
      </Box>
    );
  }

  const designerPrimaryAction = (!isHomeowner && isDesignerProfileComplete && previousDesignerProfile)
    ? {
        label: "View Results",
        onClick: () => setViewResultsDialogOpen(true),
        variant: "contained" as const,
        color: "primary" as const,
      }
    : undefined;

  const homeownerPrimaryAction = (isHomeownerProfileComplete && isHomeowner && homeownerProfile)
    ? {
        label: "View Results",
        onClick: async () => {
          // Refetch the latest profile data before opening the dialog
          if (hasPreviousHomeownerProfileForQuery) {
            console.log('🔄 Refetching homeowner profile data before showing results...');
            await refetchHomeownerProfile();
          }
          setViewHomeownerResultsDialogOpen(true);
        },
        variant: "contained" as const,
        color: "primary" as const,
      }
    : undefined;

  const headerPrimaryAction = designerPrimaryAction || homeownerPrimaryAction;

  const designerSecondaryAction = (!isHomeowner &&
    isDesignerProfileComplete &&
    previousDesignerProfile &&
    !isRetakeInProgress)
    ? {
        label: "Re-take",
        onClick: startDesignerRetake,
        variant: "outlined" as const,
        color: "secondary" as const,
      }
    : undefined;

  const homeownerSecondaryAction = (isHomeowner &&
    isHomeownerProfileComplete &&
    hasPreviousHomeownerProfile)
    ? {
        label: "Re-take",
        onClick: startHomeownerRetake,
        variant: "outlined" as const,
        color: "secondary" as const,
      }
    : undefined;

  const headerSecondaryAction = designerSecondaryAction || homeownerSecondaryAction;

  return (
    <Box>
      <DashboardHeader 
        title="Matching Profile" 
        primaryAction={headerPrimaryAction}
        secondaryAction={headerSecondaryAction}
      />
      <MatchingProfileSteps
        userType={isHomeowner ? "homeowner" : "designer"}
        currentStep={mainStep}
        showProgress={mainStep !== 6}
      >
        {content}
      </MatchingProfileSteps>

      {/* View Results Dialog - Designer */}
      {hasPreviousDesignerProfile && !isHomeowner && previousDesignerProfile && (
        <Dialog
          open={viewResultsDialogOpen}
          onClose={() => setViewResultsDialogOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              maxHeight: '90vh',
            }
          }}
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            pb: 2
          }}>
            <Typography variant="h5" fontWeight={600}>
              Matching Profile Results
            </Typography>
            <IconButton
              onClick={() => setViewResultsDialogOpen(false)}
              sx={{ ml: 2 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 0 }}>
            <DesignerMatchingProfileRetakeSummary
              profile={previousDesignerProfile}
              loading={designerProfileLoading || designerStepStatusLoading}
              currentSection="all"
            />
          </DialogContent>
        </Dialog>
      )}
      
      {/* View Results Dialog - Homeowner */}
      {isHomeownerProfileComplete && isHomeowner && (
        <Dialog
          open={viewHomeownerResultsDialogOpen}
          onClose={() => setViewHomeownerResultsDialogOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              maxHeight: '90vh',
            }
          }}
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            pb: 2
          }}>
            <Typography variant="h5" fontWeight={600}>
              Matching Profile Results
            </Typography>
            <IconButton
              onClick={() => setViewHomeownerResultsDialogOpen(false)}
              sx={{ ml: 2 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 0 }}>
            {homeownerProfileLoading ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <CircularProgress sx={{ color: '#00A06D' }} />
              </Box>
            ) : homeownerProfileData?.getHomeOwnerMatchingProfile ? (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                  Your Matching Profile Answers
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Review your answers below. You can retake the matching profile at any time.
                </Typography>
                {(() => {
                  const profile = homeownerProfileData.getHomeOwnerMatchingProfile;
                  return (
                    <HomeownerMatchingProfileRetakeSummary
                      profile={profile}
                      loading={homeownerProfileLoading}
                      currentSection="all"
                    />
                  );
                })()}
              </Box>
            ) : (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No profile data available.
                </Typography>
              </Box>
            )}
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
};
